import BillingPaymentCorrectionDialog from "./BillingPaymentCorrectionDialog";
import BillingPaymentAuditTrail from "./BillingPaymentAuditTrail";
import BillingPaymentAllocationPreview, { BillingPaymentSummary } from "./BillingPaymentAllocationPreview";
import { getSelectedPaymentSnapshot, buildPaymentAllocationPreview, comparePaymentSelection, readCompletePaymentSelection, validatePaymentEvidenceEntries } from "./billingPaymentWorkflow";
import BillingArchivedEvidence from "./BillingArchivedEvidence";
import { ESliceAuth } from "../../store/slices/authSlice";
import { useAppSelector } from "../../hook/store";
import { ESliceMenu } from "../../store/slices/menuSlice";
import { useSnackbar } from "notistack";
import { billingRelatedUrl, canApproveEvidence, createPaymentSubmission, formatPaymentInput, isPdfEvidence, mergeInvoicePages, newBillingRequestId, resolveCustomerRound, validPaymentDate } from "./billingAdminFlow";
import { formatBangkokDateTime, formatThaiCalendarDate } from '../../utilities/bangkokDateTime';
import React from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Slider,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
  TablePagination,
} from "@mui/material";
import dayjs from "dayjs";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import PaidIcon from "@mui/icons-material/Paid";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import FolderOpenOutlinedIcon from "@mui/icons-material/FolderOpenOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import ZoomInOutlinedIcon from "@mui/icons-material/ZoomInOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import NavigationBar from "../../components/global/Layout/MainLayout/NavigationBar";
import { ActionPaper } from "../../components/global/Widget";
import { BasicTable } from "../../components/global/table";
import { useLocation, useNavigate } from "react-router-dom";
import {
  useReadBillingPaymentSelection,
  useApproveBillingPayment,
  useCreateBillingPayment,
  useGetBillingCustomerInvoices,
  useGetBillingPaymentDetail,
  useGetBillingPayments,
  useGetPeriodRounds,
  useExportBillingReport,
  useDeleteBillingPayment,
  useRejectBillingPayment,
  useUpdateBillingPayment,
  useUpdatePaymentEvidenceVerification,
} from "../../hook/query/useBilling";
import {
  ICreatePaymentDto,
  TBillingPaymentReviewStatus,
  IBillingCustomerInvoice,
  IBillingPayment,
  IPaymentEvidence,
  IPaymentVerificationSummary,
  IPaymentInvoiceAllocation,
  TPaymentMethod,
} from "../../compiler/billing.interface";
import {
  getSelectablePaymentInvoices,
  getPaymentDialogOutstandingTotal,
  getPaymentDialogRows,
  getPaymentReviewEvidenceAmountState,
  isBillingPaymentAmountInvalid,
  isPaymentInvoiceSelectable,
  shouldBlockPaymentSubmissionForMissingEvidence,
  shouldBlockPaymentReviewApproval,
  shouldRequirePaymentEvidence,
} from "./billingPaymentSelection";

interface IPendingPaymentRecovery {
  payload: ICreatePaymentDto & { client_request_id: string };
  files: Array<{ name: string; size: number; type: string }>;
}

interface IPaymentEvidenceFile {
  id: string;
  file: File;
  previewUrl: string;
  amount: string;
  note: string;
  transferDate: string;
  transferReference: string;
}

interface IPaymentDialogState {
  open: boolean;
  rows?: IBillingCustomerInvoice[];
  invoiceUuids: string[];
}

interface IPreviewDialogState {
  open: boolean;
  imageUrl: string;
  title: string;
  isPdf?: boolean;
  ownsObjectUrl?: boolean;
}

interface ICancelPaymentDialogState {
  open: boolean;
  payment: IBillingPayment | null;
}

interface IResubmitPaymentDialogState {
  open: boolean;
  payment: IBillingPayment | null;
}

interface IEvidenceVerificationEdit {
  transfer_amount: string;
  transfer_date: string;
  transfer_reference: string;
  notes: string;
}

type TBillingDetailTab = "invoices" | "review" | "history";
type TPaymentHistoryStatusFilter = "all" | TBillingPaymentReviewStatus;
type TStatusChipTone = "success" | "info" | "warning" | "pending" | "error" | "neutral";

const formatCurrency = (amount: number) =>
  amount.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const statusChipSx: Record<TStatusChipTone, object> = {
  success: {
    bgcolor: "#e8f5e9",
    color: "#1b5e20",
    borderColor: "#81c784",
  },
  info: {
    bgcolor: "#e0f7fa",
    color: "#006064",
    borderColor: "#4dd0e1",
  },
  warning: {
    bgcolor: "#fff8e1",
    color: "#8a5a00",
    borderColor: "#f6c343",
  },
  pending: {
    bgcolor: "#e3f2fd",
    color: "#0d47a1",
    borderColor: "#64b5f6",
  },
  error: {
    bgcolor: "#ffebee",
    color: "#b71c1c",
    borderColor: "#ef9a9a",
  },
  neutral: {
    bgcolor: "#f3f4f6",
    color: "#374151",
    borderColor: "#cbd5e1",
  },
};

const getStatusChipSx = (tone: TStatusChipTone) => ({
  ...statusChipSx[tone],
  fontWeight: 700,
});

const getPaymentStatusLabel = (status: string): string => {
  if (status === "paid") return "ชำระแล้ว";
  if (status === "partial") return "ชำระบางส่วน";
  return "รอชำระ";
};

const getPaymentStatusTone = (status: string): TStatusChipTone => {
  if (status === "paid") return "success";
  if (status === "partial") return "info";
  return "warning";
};

const getBillingPaymentReviewStatusLabel = (status?: string): string => {
  if (status === "approved" || status === "confirmed" || status === "completed") {
    return "อนุมัติแล้ว";
  }
  if (status === "rejected") return "ไม่อนุมัติ";
  if (status === "cancelled") return "ยกเลิก";
  return "รอตรวจสอบ";
};

const getBillingPaymentReviewStatusTone = (status?: string): TStatusChipTone => {
  if (status === "approved" || status === "confirmed" || status === "completed") {
    return "success";
  }
  if (status === "rejected") return "error";
  if (status === "cancelled") return "neutral";
  return "warning";
};

const getPaymentUuid = (payment: IBillingPayment): string =>
  payment.payment_uuid || payment.uuid || "";

const isBillingPaymentCancellable = (status?: string): boolean =>
  status === "approved" || status === "confirmed";

const isBillingPaymentResubmittable = (status?: string): boolean => status === "rejected";

const paymentHistoryStatusOptions: Array<{
  value: TPaymentHistoryStatusFilter;
  label: string;
}> = [
  { value: "all", label: "ทั้งหมด" },
  { value: "submitted", label: "รอตรวจสอบ" },
  { value: "approved", label: "อนุมัติแล้ว" },
  { value: "rejected", label: "ไม่อนุมัติ" },
  { value: "cancelled", label: "ยกเลิก" },
  { value: "confirmed", label: "ยืนยันแล้ว" },
];

const formatPaymentAuditDate = (date?: string | null): string =>
  date ? formatBangkokDateTime(date, "DD/MM/BBBB HH:mm") : "";

const getBillingPaymentAuditInfo = (payment: IBillingPayment) => {
  if (payment.status === "rejected") {
    return {
      reasonLabel: "เหตุผลไม่อนุมัติ",
      reason: payment.rejection_reason || "",
      actorLabel: "ไม่อนุมัติโดย",
      actor: payment.rejected_by || "",
      occurredAt: formatPaymentAuditDate(payment.rejected_at),
      color: "error.main",
      bg: "rgba(211, 47, 47, 0.06)",
    };
  }

  if (payment.status === "cancelled") {
    return {
      reasonLabel: "เหตุผลยกเลิก",
      reason: payment.cancellation_reason || "",
      actorLabel: "ยกเลิกโดย",
      actor: payment.cancelled_by || "",
      occurredAt: formatPaymentAuditDate(payment.cancelled_at),
      color: "text.secondary",
      bg: "rgba(100, 116, 139, 0.08)",
    };
  }

  return null;
};

const getEvidenceUuid = (evidence: IPaymentEvidence): string =>
  evidence.file_uuid || evidence.uuid || evidence.file_name;

const getEvidenceUploadedAt = (evidence: IPaymentEvidence): string | undefined =>
  evidence.uploaded_at || evidence.created_at;

const getEvidenceTransferAmount = (evidence: IPaymentEvidence): number | null => {
  if (evidence.transfer_amount === undefined || evidence.transfer_amount === null) return null;
  const amount = Number(evidence.transfer_amount);
  return Number.isFinite(amount) ? amount : null;
};

const getEvidenceVerificationEdit = (evidence: IPaymentEvidence): IEvidenceVerificationEdit => {
  const amount = getEvidenceTransferAmount(evidence);
  return {
    transfer_amount: amount === null ? "" : String(amount),
    transfer_date: evidence.transfer_date ? dayjs(evidence.transfer_date).format("YYYY-MM-DD") : "",
    transfer_reference: evidence.transfer_reference || "",
    notes: evidence.notes || "",
  };
};

const formatEvidenceSource = (source?: string | null): string => {
  if (source === "customer" || source === "portal") return "ลูกค้า";
  if (source === "admin") return "Admin";
  return "ไม่ระบุ";
};

const getEvidenceOcrStatus = (status?: string | null) => {
  if (status === "completed") return { label: "OCR แล้ว", color: "success" as const };
  if (status === "processing") return { label: "กำลัง OCR", color: "info" as const };
  if (status === "failed") return { label: "OCR ไม่สำเร็จ", color: "error" as const };
  return { label: "ยังไม่ OCR", color: "default" as const };
};

const getPaymentVerificationStatus = (summary?: IPaymentVerificationSummary) => {
  if (!summary) return { label: "รอตรวจยอด", color: "default" as const };
  if (summary.amount_matches_payment === true) {
    return { label: "ยอดหลักฐานตรง", color: "success" as const };
  }
  if (summary.amount_matches_payment === false) {
    return { label: "ยอดหลักฐานไม่ตรง", color: "warning" as const };
  }
  if (summary.evidence_file_count === 0) return { label: "ไม่มีหลักฐาน", color: "error" as const };
  return { label: "รอตรวจหลักฐาน", color: "info" as const };
};

const formatOcrStatusCounts = (summary?: IPaymentVerificationSummary): string => {
  if (!summary || Object.keys(summary.ocr_status_counts || {}).length === 0) return "OCR: -";
  return Object.entries(summary.ocr_status_counts)
    .map(([status, count]) => `${getEvidenceOcrStatus(status).label} ${count}`)
    .join(", ");
};

const BillingCompanyInvoiceListPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const permission = useAppSelector(state => state[ESliceMenu.menu]);
  const actor = useAppSelector(state => state[ESliceAuth.AuthInfo]);
  const [pendingRecovery, setPendingRecovery] = React.useState<IPendingPaymentRecovery | null>(null);
  const [recoveryFiles, setRecoveryFiles] = React.useState<File[]>([]);
  const [recoveryNotFound, setRecoveryNotFound] = React.useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const reportMutation = useExportBillingReport();
  const [tablePage, setTablePage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(50);
  const [paymentPage, setPaymentPage] = React.useState(0);
  const [billingDetailTab, setBillingDetailTab] = React.useState<TBillingDetailTab>("invoices");
  const [search, setSearch] = React.useState("");
  const [paymentHistoryStatusFilter, setPaymentHistoryStatusFilter] = React.useState<TPaymentHistoryStatusFilter>("all");
  const [invoiceCache, setInvoiceCache] = React.useState<Record<string, IBillingCustomerInvoice>>({});
  const [paymentDateInput, setPaymentDateInput] = React.useState("");
  const [submissionUncertain, setSubmissionUncertain] = React.useState(false);
  const submission = React.useRef(createPaymentSubmission<ICreatePaymentDto>(newBillingRequestId));
  const readSelectionMutation = useReadBillingPaymentSelection();
  const [selectionNotice, setSelectionNotice] = React.useState("");
  const [selectionDialogOpen, setSelectionDialogOpen] = React.useState(false);
  const [createdPayment, setCreatedPayment] = React.useState<{ uuid: string; id: string } | null>(null);
  const selectionOperation = React.useRef(0);
  React.useEffect(() => () => { selectionOperation.current += 1; }, []);

  React.useEffect(() => {
    if (!submissionUncertain) return;
    const warn = (event: BeforeUnloadEvent) => { event.preventDefault(); event.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [submissionUncertain]);
  const [evidenceChecked, setEvidenceChecked] = React.useState<Record<string, boolean>>({});
  const [evidenceDirty, setEvidenceDirty] = React.useState<Record<string, boolean>>({});
  const query = React.useMemo(() => new URLSearchParams(location.search), [location.search]);

  const defaultRound = query.get("round") || "";
  const startDate = query.get("start_date") || "";
  const endDate = query.get("end_date") || "";
  const defaultCustomerNo = query.get("customerNo") || "";
  const defaultCustomerName = query.get("customerName") || "";
  const requestStorageKey = `billing.pending:${actor.username}:${localStorage.getItem('currentFranchiseUuid') || actor.company_uuid}:${defaultCustomerNo}:${startDate}:${endDate}`;
  const correctionStorageKey = `${requestStorageKey}:correction`;
  const [correctionUuid, setCorrectionUuid] = React.useState('');
  const [correctionRecoveryUuid, setCorrectionRecoveryUuid] = React.useState('');
  const refreshCorrectionRecovery = () => {
    try { const saved = JSON.parse(sessionStorage.getItem(correctionStorageKey) || 'null'); setCorrectionRecoveryUuid(saved?.uuid || ''); }
    catch { setCorrectionRecoveryUuid('unreadable'); }
  };
  React.useEffect(() => { setCorrectionUuid(''); refreshCorrectionRecovery(); }, [correctionStorageKey]);
  const paymentContextRef = React.useRef(requestStorageKey);
  paymentContextRef.current = requestStorageKey;
  const selectionContext = `${requestStorageKey}|${search}`;
  const selectionContextRef = React.useRef(selectionContext);
  selectionContextRef.current = selectionContext;
  React.useEffect(() => {
    selectionOperation.current += 1;
    setSelectionNotice(""); setSelectionDialogOpen(false); setCreatedPayment(null);
    setSelectedInvoiceUuids([]); setInvoiceCache({});
    submission.current.reset(); setSubmissionUncertain(false);
    setPaymentDialog({ open: false, invoiceUuids: [] });
    setEvidenceFiles(previous => { previous.forEach(file => URL.revokeObjectURL(file.previewUrl)); return []; });
    try {
      const saved = sessionStorage.getItem(requestStorageKey);
      setPendingRecovery(saved ? JSON.parse(saved) : null);
      setRecoveryFiles([]); setRecoveryNotFound(false);
    } catch { setPendingRecovery(null); }
  }, [requestStorageKey]);
  const recoveryQuery = useGetBillingPayments({ client_request_id: pendingRecovery?.payload.client_request_id,
    customer_code: defaultCustomerNo, limit: 1 }, false);
  const reconcilePending = async () => {
    const result = await recoveryQuery.refetch();
    if (result.isError) return;
    const found = result.data?.data?.payments?.[0];
    if (found) {
      sessionStorage.removeItem(requestStorageKey); setPendingRecovery(null); setSubmissionUncertain(false);
      submission.current.reset(); setPaymentDialog({ open: false, invoiceUuids: [] });
      setReviewPaymentUuid(getPaymentUuid(found));
    } else setRecoveryNotFound(true);
  };


  // --- Fetch billing rounds for dropdown ---
  const { data: roundsResponse } = useGetPeriodRounds(startDate, endDate, !!startDate && !!endDate);
  const billingRoundsList = roundsResponse?.data?.rounds ?? [];

  // --- Active round dates ---
  const activeStartDate = startDate;
  const activeEndDate = endDate;
  const activeRoundLabel = defaultRound || billingRoundsList[0]?.round_label || "";
  const activeBillingRound = React.useMemo(() => {
    const candidates = billingRoundsList.filter(
      (round) => round.start_date === activeStartDate && round.end_date === activeEndDate
    );
    return resolveCustomerRound(candidates, defaultCustomerNo);
  }, [activeEndDate, activeStartDate, billingRoundsList, defaultCustomerNo]);
  const activeBillingRoundUuid = activeBillingRound?.id || "";

  // --- Fetch invoices for the selected customer ---
  const { data: invoicesResponse, isLoading, isFetching: invoiceFetching, isPreviousData: invoiceStale, isError: invoiceError } = useGetBillingCustomerInvoices({
    customer_code: defaultCustomerNo,
    start_date: activeStartDate,
    end_date: activeEndDate,
    page: tablePage + 1,
    limit: rowsPerPage,
    search: search || undefined,
  });
  const invoices = React.useMemo(() => invoiceStale ? [] : invoicesResponse?.data?.invoices ?? [], [invoicesResponse, invoiceStale]);
  const invoiceSummary = invoicesResponse?.data?.summary;
  const invoiceTotal = invoicesResponse?.data?.pagination.total ?? 0;
  const cachedScope = React.useRef("");
  React.useEffect(() => {
    if (invoiceStale) return;
    const scopeKey = `${defaultCustomerNo}|${activeStartDate}|${activeEndDate}`;
    const sameScope = cachedScope.current === scopeKey;
    cachedScope.current = scopeKey;
    setInvoiceCache(cache => mergeInvoicePages(sameScope ? cache : {}, invoices));
  }, [invoices, invoiceStale, defaultCustomerNo, activeStartDate, activeEndDate]);
  const customerInfo = invoicesResponse?.data?.customer;

  // --- Create payment mutation ---
  const createPaymentMutation = useCreateBillingPayment();
  const approvePaymentMutation = useApproveBillingPayment();
  const rejectPaymentMutation = useRejectBillingPayment();
  const deletePaymentMutation = useDeleteBillingPayment();
  const updatePaymentMutation = useUpdateBillingPayment();
  const updateEvidenceVerificationMutation = useUpdatePaymentEvidenceVerification();

  const paymentsRoundKey =
    activeStartDate && activeEndDate ? `${activeStartDate} - ${activeEndDate}` : undefined;
  const { data: paymentsResponse, isLoading: isPaymentsLoading } = useGetBillingPayments(
    {
      billing_round_uuid: activeBillingRoundUuid,
      customer_code: defaultCustomerNo,
      billing_round: paymentsRoundKey,
      limit: 25,
      page: paymentPage + 1,
      status: billingDetailTab === "review" ? "submitted" : paymentHistoryStatusFilter === "all" ? undefined : paymentHistoryStatusFilter,
    },
    !!defaultCustomerNo && !!paymentsRoundKey && !!activeBillingRoundUuid
  );
  const billingPayments: IBillingPayment[] = paymentsResponse?.data?.payments ?? [];

  const [selectedInvoiceUuids, setSelectedInvoiceUuids] = React.useState<string[]>([]);
  const [reviewPaymentUuid, setReviewPaymentUuid] = React.useState<string>("");
  const [rejectReasonInput, setRejectReasonInput] = React.useState<string>("");
  const [evidenceVerificationEdits, setEvidenceVerificationEdits] = React.useState<
    Record<string, IEvidenceVerificationEdit>
  >({});

  const [paymentDialog, setPaymentDialog] = React.useState<IPaymentDialogState>({
    open: false,
    invoiceUuids: [],
  });
  const [previewDialog, setPreviewDialog] = React.useState<IPreviewDialogState>({
    open: false,
    imageUrl: "",
    title: "",
  });
  const [cancelPaymentDialog, setCancelPaymentDialog] = React.useState<ICancelPaymentDialogState>({
    open: false,
    payment: null,
  });
  const [cancelPaymentReasonInput, setCancelPaymentReasonInput] = React.useState<string>("");
  const [resubmitPaymentDialog, setResubmitPaymentDialog] =
    React.useState<IResubmitPaymentDialogState>({
      open: false,
      payment: null,
    });
  const [replaceEvidenceConfirmed, setReplaceEvidenceConfirmed] = React.useState(false);
  const { data: resubmitDetailResponse, isLoading: resubmitDetailLoading } = useGetBillingPaymentDetail(
    resubmitPaymentDialog.payment ? getPaymentUuid(resubmitPaymentDialog.payment) : '', resubmitPaymentDialog.open);
  const resubmitActiveEvidence = resubmitDetailResponse?.data?.evidence_files || resubmitDetailResponse?.data?.evidenceFiles || [];
  const [resubmitPaymentReferenceInput, setResubmitPaymentReferenceInput] =
    React.useState<string>("");
  const [resubmitPaymentNotesInput, setResubmitPaymentNotesInput] = React.useState<string>("");
  const [resubmitEvidenceFiles, setResubmitEvidenceFiles] = React.useState<IPaymentEvidenceFile[]>(
    []
  );

  const [paymentAmountInput, setPaymentAmountInput] = React.useState<string>("");
  const [paymentMethodInput, setPaymentMethodInput] = React.useState<TPaymentMethod>("transfer");
  const [paymentReferenceInput, setPaymentReferenceInput] = React.useState<string>("");
  const [paymentNotesInput, setPaymentNotesInput] = React.useState<string>("");
  const [evidenceFiles, setEvidenceFiles] = React.useState<IPaymentEvidenceFile[]>([]);
  const [isDragOver, setIsDragOver] = React.useState<boolean>(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const resubmitFileInputRef = React.useRef<HTMLInputElement>(null);
  const [zoomLevel, setZoomLevel] = React.useState<number>(1);

  const { data: reviewPaymentResponse, isLoading: isReviewPaymentLoading } =
    useGetBillingPaymentDetail(reviewPaymentUuid, !!reviewPaymentUuid);
  const reviewPayment = reviewPaymentResponse?.data;
  const reviewEvidenceFiles: IPaymentEvidence[] = React.useMemo(
    () => reviewPayment?.evidence_files || reviewPayment?.evidenceFiles || [],
    [reviewPayment]
  );
  const reviewInvoicePayments: IPaymentInvoiceAllocation[] = React.useMemo(
    () => reviewPayment?.invoice_payments || reviewPayment?.invoiceAllocations || [],
    [reviewPayment]
  );
  const reviewEvidenceAmountState = React.useMemo(
    () =>
      getPaymentReviewEvidenceAmountState(
        reviewEvidenceFiles,
        Number(reviewPayment?.total_paid_amount || 0)
      ),
    [reviewEvidenceFiles, reviewPayment?.total_paid_amount]
  );
  const reviewVerificationSummary = reviewPayment?.verification_summary;
  const reviewVerificationStatus = getPaymentVerificationStatus(reviewVerificationSummary);
  React.useEffect(() => {
    setEvidenceVerificationEdits((prev) => {
      const next: Record<string, IEvidenceVerificationEdit> = {};
      reviewEvidenceFiles.forEach((evidence) => {
        const evidenceUuid = getEvidenceUuid(evidence);
        next[evidenceUuid] = prev[evidenceUuid] || getEvidenceVerificationEdit(evidence);
      });
      return next;
    });
  }, [reviewEvidenceFiles]);
  const shouldBlockReviewApproval = !reviewPayment ||
    !canApproveEvidence(reviewEvidenceFiles, Number(reviewPayment.total_paid_amount), reviewPayment.payment_method) ||
    Object.values(evidenceDirty).some(Boolean) || updateEvidenceVerificationMutation.isLoading;


  const filteredData = invoices;

  const submittedPayments = React.useMemo(
    () => billingPayments.filter((payment) => payment.status === "submitted"),
    [billingPayments]
  );
  const submittedPaymentInvoiceUuidSet = React.useMemo(() => {
    const invoiceUuids = submittedPayments.flatMap((payment) =>
      (payment.invoice_payments || payment.invoiceAllocations || []).map(
        (allocation) => allocation.invoice_uuid
      )
    );
    return new Set(invoiceUuids.filter(Boolean));
  }, [submittedPayments]);
  React.useEffect(() => {
    if (submittedPaymentInvoiceUuidSet.size === 0) return;
    setSelectedInvoiceUuids((prev) =>
      prev.filter((uuid) => !submittedPaymentInvoiceUuidSet.has(uuid))
    );
  }, [submittedPaymentInvoiceUuidSet]);

  const selectedRows = React.useMemo(
    () => { const selected = new Set(selectedInvoiceUuids); return Object.values(invoiceCache).filter(row => selected.has(row.uuid)); },
    [invoiceCache, selectedInvoiceUuids]
  );
  const paymentDialogRows = React.useMemo(
    () => getPaymentDialogRows(paymentDialog.rows || Object.values(invoiceCache), paymentDialog.invoiceUuids),
    [invoiceCache, paymentDialog.invoiceUuids, paymentDialog.rows]
  );
  const paymentDialogOutstandingTotal = React.useMemo(
    () => getPaymentDialogOutstandingTotal(paymentDialog.rows || Object.values(invoiceCache), paymentDialog.invoiceUuids),
    [invoiceCache, paymentDialog.invoiceUuids, paymentDialog.rows]
  );
  const selectableInvoices = React.useMemo(
    () => getSelectablePaymentInvoices(filteredData, submittedPaymentInvoiceUuidSet),
    [filteredData, submittedPaymentInvoiceUuidSet]
  );

  const evidenceTotalAmount = React.useMemo(
    () => evidenceFiles.reduce((sum, f) => sum + (Number(f.amount) || 0), 0),
    [evidenceFiles]
  );
  const hasEvidenceAmounts = React.useMemo(
    () => evidenceFiles.some((f) => f.amount.trim() !== "" && Number.isFinite(Number(f.amount))),
    [evidenceFiles]
  );

  const totalAmount = Number(invoiceSummary?.total_amount || 0);
  const paidAmount = Number(invoiceSummary?.paid_amount || 0);
  const paidInvoiceCount = Number(invoiceSummary?.paid_invoices || 0);
  const selectedTotalAmount = selectedRows.reduce((sum, item) => sum + Math.round(Number(item.outstanding_amount) * 100), 0) / 100;
  const paymentAmountValue = Number(paymentAmountInput || 0);
  const allocationPreview = React.useMemo(() => buildPaymentAllocationPreview(paymentDialogRows, paymentAmountValue), [paymentDialogRows, paymentAmountValue]);
  const evidenceValidation = React.useMemo(() => validatePaymentEvidenceEntries(evidenceFiles, paymentAmountValue, shouldRequirePaymentEvidence(paymentMethodInput)), [evidenceFiles, paymentAmountValue, paymentMethodInput]);
  const hiddenSelectedCount = selectedRows.filter(row => search.trim() && !row.invoice_number.toLowerCase().includes(search.trim().toLowerCase())).length;

  const isPaymentAmountPrecisionInvalid = Number.isFinite(paymentAmountValue) && Math.abs(paymentAmountValue * 100 - Math.round(paymentAmountValue * 100)) >= 0.000001;
  const isPaymentAmountInvalid = isPaymentAmountPrecisionInvalid || isBillingPaymentAmountInvalid(
    paymentDialog.open,
    paymentAmountValue,
    paymentDialogOutstandingTotal
  );
  const isPaymentEvidenceRequired = shouldRequirePaymentEvidence(paymentMethodInput);
  const isPaymentEvidenceMissing = shouldBlockPaymentSubmissionForMissingEvidence(
    paymentMethodInput,
    evidenceFiles.length
  );
  const paymentAmountHelperText = isPaymentAmountInvalid
    ? isPaymentAmountPrecisionInvalid
      ? "ระบุยอดรับชำระทศนิยมไม่เกิน 2 ตำแหน่ง"
      : paymentAmountValue > paymentDialogOutstandingTotal
      ? "ยอดชำระเกินยอดค้างของบิลที่เลือก"
      : "ยอดชำระต้องมากกว่า 0"
    : evidenceFiles.length > 0
      ? hasEvidenceAmounts
        ? `ยอดรวมจากหลักฐาน: ${formatCurrency(evidenceTotalAmount)}`
        : "ระบุยอดต่อไฟล์หลักฐานได้ เพื่อช่วยตรวจยอดตอนอนุมัติ"
      : undefined;
  const paymentSummary = paymentsResponse?.data?.summary;
  const paymentHistoryStatusCounts = { all: Number(paymentSummary?.total_payments || 0), ...paymentSummary?.status_counts };

  const filteredBillingPayments = React.useMemo(() => {
    if (paymentHistoryStatusFilter === "all") return billingPayments;
    return billingPayments.filter((payment) => payment.status === paymentHistoryStatusFilter);
  }, [billingPayments, paymentHistoryStatusFilter]);
  const filteredBillingPaymentTotalAmount = React.useMemo(
    () =>
      filteredBillingPayments.reduce(
        (sum, payment) => sum + Number(payment.total_paid_amount || 0),
        0
      ),
    [filteredBillingPayments]
  );
  const filteredBillingPaymentInvoiceCount = React.useMemo(
    () =>
      filteredBillingPayments.reduce((sum, payment) => sum + Number(payment.invoice_count || 0), 0),
    [filteredBillingPayments]
  );
  const filteredBillingPaymentEvidenceCount = React.useMemo(
    () =>
      filteredBillingPayments.reduce(
        (sum, payment) =>
          sum + (payment.evidence_files?.length || payment.evidenceFiles?.length || 0),
        0
      ),
    [filteredBillingPayments]
  );
  const paymentHistoryHeaders = [
    { name: "เลขที่รับชำระ", props: { align: "left" as const, width: "16%" } },
    { name: "วันที่ชำระ", props: { align: "center" as const, width: "10%" } },
    { name: "ยอดโอน", props: { align: "right" as const, width: "12%" } },
    { name: "Invoice", props: { align: "center" as const, width: "8%" } },
    { name: "หลักฐาน", props: { align: "center" as const, width: "10%" } },
    { name: "สถานะ", props: { align: "center" as const, width: "18%" } },
    { name: "Ref", props: { align: "left" as const, width: "12%" } },
    { name: "เครื่องมือ", props: { align: "center" as const, width: "14%" } },
  ];

  const isAllVisibleSelected =
    selectableInvoices.length > 0 &&
    selectableInvoices.every((row) => selectedInvoiceUuids.includes(row.uuid));
  const isSomeVisibleSelected =
    selectableInvoices.some((row) => selectedInvoiceUuids.includes(row.uuid)) &&
    !isAllVisibleSelected;


  React.useEffect(() => {
    setTablePage(0);
  }, [search]);

  const pagedData = filteredData;
  React.useEffect(() => { setPaymentPage(0); }, [billingDetailTab, paymentHistoryStatusFilter]);
  React.useEffect(() => { setSelectedInvoiceUuids([]); setTablePage(0); setPaymentPage(0); }, [defaultCustomerNo, activeStartDate, activeEndDate]);

  const tableHeaders = [
    {
      name: (
        <Checkbox
          checked={isAllVisibleSelected}
          indeterminate={isSomeVisibleSelected}
          onChange={toggleSelectAllVisible}
          inputProps={{ "aria-label": "เลือกบิลที่รับชำระได้ในหน้านี้" }}
          disabled={readSelectionMutation.isLoading || invoiceFetching}
          size="small"
        />
      ),
      props: { align: "center" as const, width: "5%" },
    },
    { name: "InvoiceNo", props: { align: "left" as const, width: "18%" } },
    { name: "วันที่", props: { align: "center" as const, width: "12%" } },
    { name: "จำนวนเงิน", props: { align: "right" as const, width: "14%" } },
    { name: "จำนวนชำระ", props: { align: "right" as const, width: "14%" } },
    { name: "ค้างชำระ", props: { align: "right" as const, width: "14%" } },
    { name: "สถานะ", props: { align: "center" as const, width: "10%" } },
    { name: "เครื่องมือ", props: { align: "center" as const, width: "13%" } },
  ];

  function toggleSelectAllVisible() {
    if (readSelectionMutation.isLoading || invoiceFetching) return;
    setSelectionNotice("");
    if (isAllVisibleSelected) {
      setSelectedInvoiceUuids((prev) =>
        prev.filter((uuid) => !selectableInvoices.some((row) => row.uuid === uuid))
      );
      return;
    }

    setSelectedInvoiceUuids((prev) => {
      const visibleUuids = selectableInvoices.map((row) => row.uuid);
      return Array.from(new Set([...prev, ...visibleUuids]));
    });
  }

  const toggleSelectOne = (row: IBillingCustomerInvoice) => {
    if (readSelectionMutation.isLoading || !isPaymentInvoiceSelectable(row, submittedPaymentInvoiceUuidSet)) return;
    setSelectedInvoiceUuids((prev) =>
      prev.includes(row.uuid) ? prev.filter((id) => id !== row.uuid) : [...prev, row.uuid]
    );
  };

  const addEvidenceFiles = (files: FileList | File[]) => {
    const supportedFiles = Array.from(files).filter(
      (file) => file.type.startsWith("image/") || file.type === "application/pdf"
    );
    const newEvidence: IPaymentEvidenceFile[] = supportedFiles.map((file) => ({
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
      file,
      previewUrl: URL.createObjectURL(file),
      amount: "",
      note: "",
      transferDate: paymentDateInput,
      transferReference: "",
    }));
    if (newEvidence.length !== Array.from(files).length) enqueueSnackbar("รองรับเฉพาะรูปภาพหรือ PDF", { variant: "warning" });
    if (newEvidence.some(item => item.file.size > 10 * 1024 * 1024) || evidenceFiles.length + newEvidence.length > 10) {
      newEvidence.forEach(item => URL.revokeObjectURL(item.previewUrl));
      enqueueSnackbar("แนบได้สูงสุด 10 ไฟล์ ขนาดไม่เกิน 10 MB ต่อไฟล์", { variant: "warning" }); return;
    }
    setEvidenceFiles((prev) => {
      const known = new Set(prev.map(item => `${item.file.name}|${item.file.size}|${item.file.lastModified}`));
      const unique = newEvidence.filter(item => {
        const key = `${item.file.name}|${item.file.size}|${item.file.lastModified}`;
        if (known.has(key)) { URL.revokeObjectURL(item.previewUrl); return false; }
        known.add(key); return true;
      });
      return [...prev, ...unique];
    });
  };

  const removeEvidenceFile = (id: string) => {
    setEvidenceFiles((prev) => {
      const target = prev.find((f) => f.id === id);
      if (target) URL.revokeObjectURL(target.previewUrl);
      return prev.filter((f) => f.id !== id);
    });
  };

  const updateEvidenceFile = (id: string, field: "amount" | "note" | "transferDate" | "transferReference", value: string) => {
    setEvidenceFiles((prev) => prev.map((f) => (f.id === id ? { ...f, [field]: value } : f)));
  };

  const addResubmitEvidenceFiles = (files: FileList | File[]) => {
    const supportedFiles = Array.from(files).filter(
      (file) => file.type.startsWith("image/") || file.type === "application/pdf"
    );
    const newEvidence: IPaymentEvidenceFile[] = supportedFiles.map((file) => ({
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
      file,
      previewUrl: URL.createObjectURL(file),
      amount: "",
      note: "",
      transferDate: paymentDateInput,
      transferReference: "",
    }));
    if (newEvidence.some(item => item.file.size > 10 * 1024 * 1024) || resubmitEvidenceFiles.length + newEvidence.length > 10) {
      newEvidence.forEach(item => URL.revokeObjectURL(item.previewUrl));
      enqueueSnackbar("แนบได้สูงสุด 10 ไฟล์ ขนาดไม่เกิน 10 MB ต่อไฟล์", { variant: "warning" }); return;
    }
    setResubmitEvidenceFiles((prev) => [...prev, ...newEvidence]);
  };

  const removeResubmitEvidenceFile = (id: string) => {
    setResubmitEvidenceFiles((prev) => {
      const target = prev.find((f) => f.id === id);
      if (target) URL.revokeObjectURL(target.previewUrl);
      return prev.filter((f) => f.id !== id);
    });
  };

  const handleResubmitFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.length) {
      addResubmitEvidenceFiles(e.target.files);
      e.target.value = "";
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    if (e.dataTransfer.files.length) {
      addEvidenceFiles(e.dataTransfer.files);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.length) {
      addEvidenceFiles(e.target.files);
      e.target.value = "";
    }
  };

  const loadSelection = async (querySearch?: string) => {
    const response = await readSelectionMutation.mutateAsync({ customer_code: defaultCustomerNo,
      start_date: activeStartDate, end_date: activeEndDate, search: querySearch || undefined });
    return readCompletePaymentSelection(response.data);
  };

  const selectAllMatching = async () => {
    if (!permission.create || readSelectionMutation.isLoading || invoiceFetching || pendingRecovery) return;
    const context = selectionContextRef.current;
    const operation = ++selectionOperation.current;
    try {
      const rows = await loadSelection(search);
      if (context !== selectionContextRef.current || operation !== selectionOperation.current) return;
      const eligible = getSelectablePaymentInvoices(rows);
      setInvoiceCache(cache => mergeInvoicePages(cache, rows));
      setSelectedInvoiceUuids(eligible.map(row => row.uuid));
      setSelectionNotice(`เลือกบิลที่รับชำระได้ทั้งหมดตามเงื่อนไข ${eligible.length.toLocaleString()} ใบ · ยกเว้นบิลชำระครบหรือรอตรวจ ${rows.length - eligible.length} ใบ`);
    } catch (error) {
      if (context === selectionContextRef.current && operation === selectionOperation.current)
        setSelectionNotice((error as { response?: { data?: { message?: string } }; message?: string }).response?.data?.message || "โหลดบิลทั้งชุดไม่สำเร็จ กรุณาลองใหม่ ไม่ได้เปลี่ยนรายการที่เลือก");
    }
  };

  const openPaymentDialog = async (invoiceUuids: string[]) => {
    if (!permission.create || pendingRecovery || correctionRecoveryUuid || invoiceFetching || readSelectionMutation.isLoading || submissionUncertain || !invoiceUuids.length || !activeBillingRoundUuid) return;
    const context = selectionContextRef.current;
    const operation = ++selectionOperation.current;
    const previous = getSelectedPaymentSnapshot(invoiceUuids, invoiceCache, invoices);
    try {
      const current = await loadSelection();
      if (context !== selectionContextRef.current || operation !== selectionOperation.current) return;
      const { eligible: rows, changed } = comparePaymentSelection(previous, current);
      setInvoiceCache(cache => mergeInvoicePages(cache, current));
      if (changed.length || rows.length !== invoiceUuids.length) {
        setSelectedInvoiceUuids(rows.map(row => row.uuid));
        setSelectionNotice(`ยอดหรือสถานะเปลี่ยน ${changed.length} ใบ: ${changed.slice(0, 5).join(", ")}${changed.length > 5 ? " …" : ""} กรุณาดูรายการที่เลือกและกดรับชำระใหม่เพื่อยืนยันยอดล่าสุด`);
        return;
      }
      if (!rows.length) return;
      const outstandingTotal = rows.reduce((sum, row) => sum + Math.round(Number(row.outstanding_amount) * 100), 0) / 100;
      submission.current.reset();
      setPaymentDateInput(formatBangkokDateTime(new Date(), "YYYY-MM-DD"));
      setPaymentAmountInput(formatPaymentInput(outstandingTotal));
      setPaymentMethodInput("transfer"); setPaymentReferenceInput(""); setPaymentNotesInput("");
      setEvidenceFiles([]); setSelectionNotice("");
      setPaymentDialog({ open: true, invoiceUuids: rows.map(row => row.uuid), rows });
    } catch (error) {
      if (context === selectionContextRef.current && operation === selectionOperation.current)
        setSelectionNotice((error as { response?: { data?: { message?: string } }; message?: string }).response?.data?.message || "ตรวจยอดล่าสุดไม่สำเร็จ กรุณาลองใหม่ก่อนรับชำระ");
    }
  };

  const closePaymentDialog = () => {
    if (createPaymentMutation.isLoading || submissionUncertain) return;
    evidenceFiles.forEach((f) => URL.revokeObjectURL(f.previewUrl));
    setEvidenceFiles([]);
    setPaymentDialog({ open: false, invoiceUuids: [] });
  };

  const submitPaymentPayload = (payload: ICreatePaymentDto, isRetry: boolean) => {
    const marker: IPendingPaymentRecovery = { payload: { ...payload, files: undefined, client_request_id: payload.client_request_id! },
      files: (payload.files || []).map(file => ({ name: file.name, size: file.size, type: file.type })) };
    try { sessionStorage.setItem(requestStorageKey, JSON.stringify(marker)); }
    catch { enqueueSnackbar("ไม่สามารถเก็บข้อมูลกู้คืนรายการได้ กรุณาเปิดพื้นที่จัดเก็บของเบราว์เซอร์ก่อนบันทึก", { variant: "error" }); return; }
    setPendingRecovery(marker);
    setSubmissionUncertain(true);
    createPaymentMutation.mutate(payload,
      {
        onSuccess: (result) => {
          sessionStorage.removeItem(requestStorageKey);
          if (paymentContextRef.current !== requestStorageKey) return;
          const uuid = result.data.payment_uuid || result.data.uuid || "";
          setCreatedPayment({ uuid, id: result.data.payment_id });
          setBillingDetailTab("review");
          if (uuid) setReviewPaymentUuid(uuid);
          submission.current.reset();
          sessionStorage.removeItem(requestStorageKey); setPendingRecovery(null); setRecoveryNotFound(false);
          setSubmissionUncertain(false);
          evidenceFiles.forEach(file => URL.revokeObjectURL(file.previewUrl));
          setEvidenceFiles([]);
          setPaymentDialog({ open: false, invoiceUuids: [] });
          setSelectedInvoiceUuids([]);
        },
        onError: (error: any) => {
          if (paymentContextRef.current !== requestStorageKey) return;
          const status = error?.response?.status;
          if (!isRetry && status && status >= 400 && status < 500) {
            submission.current.reset(); setSubmissionUncertain(false);
            sessionStorage.removeItem(requestStorageKey); setPendingRecovery(null);
          }
        },
      });
  };

  const savePayment = () => {
    if (!permission.create || createPaymentMutation.isLoading || correctionRecoveryUuid) return;
    const frozenPayload = submission.current.peek();
    if (frozenPayload) { submitPaymentPayload(frozenPayload, true); return; }
    if (!validPaymentDate(paymentDateInput) || !defaultCustomerNo || !activeBillingRoundUuid || !allocationPreview.valid || evidenceValidation.errors.length) return;
    if (paymentDialog.invoiceUuids.some((uuid) => submittedPaymentInvoiceUuidSet.has(uuid))) return;

    const paymentAmount = Number(paymentAmountInput || 0);
    if (
      !Number.isFinite(paymentAmount) ||
      paymentAmount <= 0 ||
      paymentAmount > paymentDialogOutstandingTotal ||
      shouldBlockPaymentSubmissionForMissingEvidence(paymentMethodInput, evidenceFiles.length)
    ) {
      return;
    }

    submitPaymentPayload(submission.current.prepare({
        invoice_uuids: paymentDialog.invoiceUuids,
        expected_invoice_balances: paymentDialogRows.map(row => ({ invoice_uuid: row.uuid, outstanding_amount: Number(row.outstanding_amount) })),
        allocation: allocationPreview.allocations,
        customer_code: defaultCustomerNo,
        total_paid_amount: paymentAmount,
        payment_method: paymentMethodInput,
        payment_date: paymentDateInput,
        payment_reference: paymentReferenceInput || undefined,
        notes: paymentNotesInput || undefined,
        billing_round: paymentsRoundKey,
        billing_round_uuid: activeBillingRoundUuid,
        evidence_metadata: evidenceFiles.map((evidence) => {
          const evidenceAmount = Number(evidence.amount);
          return {
            file_name: evidence.file.name,
            transfer_amount:
              Number.isFinite(evidenceAmount) && evidenceAmount > 0
                ? evidenceAmount
                : undefined,
            transfer_date: evidence.transferDate,
            transfer_reference: evidence.transferReference.trim(),
            notes: evidence.note || undefined,
          };
        }),
        files: evidenceFiles.map((f) => f.file),
      }), false);
  };

  const openReviewPayment = (payment: IBillingPayment) => {
    const uuid = getPaymentUuid(payment);
    if (!uuid) return;
    setReviewPaymentUuid(uuid);
    setRejectReasonInput("");
  };

  const closeReviewPayment = () => {
    setReviewPaymentUuid("");
    setRejectReasonInput("");
    setEvidenceVerificationEdits({});
    setEvidenceChecked({}); setEvidenceDirty({});
  };

  const openCancelPaymentDialog = (payment: IBillingPayment) => {
    if (!permission.delete || !getPaymentUuid(payment) || !isBillingPaymentCancellable(payment.status)) return;
    setCancelPaymentDialog({ open: true, payment });
    setCancelPaymentReasonInput("");
  };

  const closeCancelPaymentDialog = () => {
    setCancelPaymentDialog({ open: false, payment: null });
    setCancelPaymentReasonInput("");
  };

  const openResubmitPaymentDialog = (payment: IBillingPayment) => {
    if (!permission.update || !getPaymentUuid(payment) || !isBillingPaymentResubmittable(payment.status)) return;
    setResubmitPaymentDialog({ open: true, payment });
    setResubmitPaymentReferenceInput(payment.payment_reference || "");
    setResubmitPaymentNotesInput(payment.notes || "");
    setResubmitEvidenceFiles([]);
    setReplaceEvidenceConfirmed(false);
  };

  const closeResubmitPaymentDialog = () => {
    resubmitEvidenceFiles.forEach((f) => URL.revokeObjectURL(f.previewUrl));
    setResubmitEvidenceFiles([]);
    setResubmitPaymentDialog({ open: false, payment: null });
    setResubmitPaymentReferenceInput("");
    setResubmitPaymentNotesInput("");
  };

  const confirmCancelPayment = () => {
    const payment = cancelPaymentDialog.payment;
    const paymentUuid = payment ? getPaymentUuid(payment) : "";
    const cancellationReason = cancelPaymentReasonInput.trim();
    if (!permission.delete || !paymentUuid || cancellationReason.length < 3) return;

    deletePaymentMutation.mutate(
      {
        uuid: paymentUuid,
        dto: { cancellation_reason: cancellationReason },
      },
      {
        onSuccess: () => {
          closeCancelPaymentDialog();
          if (reviewPaymentUuid === paymentUuid) {
            closeReviewPayment();
          }
        },
      }
    );
  };

  const confirmResubmitPayment = () => {
    const payment = resubmitPaymentDialog.payment;
    const paymentUuid = payment ? getPaymentUuid(payment) : "";
    if (!permission.update || !paymentUuid || !replaceEvidenceConfirmed || resubmitDetailLoading || !resubmitDetailResponse?.data || resubmitEvidenceFiles.length === 0) return;

    updatePaymentMutation.mutate(
      {
        uuid: paymentUuid,
        dto: {
          payment_reference: resubmitPaymentReferenceInput || undefined,
          notes: resubmitPaymentNotesInput || undefined,
          files: resubmitEvidenceFiles.map((f) => f.file),
          remove_file_uuids: resubmitActiveEvidence.map(file => file.file_uuid || file.uuid || '').filter(Boolean),
        },
      },
      {
        onSuccess: () => {
          closeResubmitPaymentDialog();
          setBillingDetailTab("review");
        },
      }
    );
  };

  const updateEvidenceVerificationField = (
    evidenceUuid: string,
    field: keyof IEvidenceVerificationEdit,
    value: string
  ) => {
    setEvidenceDirty(prev => ({ ...prev, [evidenceUuid]: true }));
    setEvidenceChecked(prev => ({ ...prev, [evidenceUuid]: false }));
    setEvidenceVerificationEdits((prev) => ({
      ...prev,
      [evidenceUuid]: {
        ...(prev[evidenceUuid] || {
          transfer_amount: "",
          transfer_date: "",
          transfer_reference: "",
          notes: "",
        }),
        [field]: value,
      },
    }));
  };

  const saveEvidenceVerification = (evidence: IPaymentEvidence) => {
    const evidenceUuid = evidence.file_uuid || evidence.uuid || "";
    if (!permission.approve || !evidenceChecked[getEvidenceUuid(evidence)] || !evidenceUuid) return;
    const edit =
      evidenceVerificationEdits[getEvidenceUuid(evidence)] || getEvidenceVerificationEdit(evidence);
    const transferAmount = Number(edit.transfer_amount);

    updateEvidenceVerificationMutation.mutate({
      evidenceUuid,
      dto: {
        ...(edit.transfer_amount.trim() !== "" &&
          Number.isFinite(transferAmount) && { transfer_amount: transferAmount }),
        ...(edit.transfer_date && { transfer_date: edit.transfer_date }),
        transfer_reference: edit.transfer_reference,
        notes: edit.notes,
        ocr_status: "manual_review",
      },
    }, { onSuccess: () => setEvidenceDirty(prev => ({ ...prev, [getEvidenceUuid(evidence)]: false })) });
  };

  const approveReviewPayment = () => {
    if (!permission.approve || !reviewPaymentUuid || shouldBlockReviewApproval) return;
    approvePaymentMutation.mutate(reviewPaymentUuid, {
      onSuccess: () => closeReviewPayment(),
    });
  };

  const rejectReviewPayment = () => {
    if (!permission.approve || !reviewPaymentUuid || rejectReasonInput.trim().length < 3) return;
    rejectPaymentMutation.mutate(
      {
        uuid: reviewPaymentUuid,
        dto: { rejection_reason: rejectReasonInput.trim() || undefined },
      },
      {
        onSuccess: () => closeReviewPayment(),
      }
    );
  };

  const openPreviewDialog = (imageUrl: string, title: string) => {
    setZoomLevel(1);
    let previewUrl = imageUrl;
    if (isPdfEvidence(imageUrl, title) && imageUrl.startsWith("data:")) {
      const encoded = imageUrl.split(",")[1];
      const bytes = Uint8Array.from(atob(encoded), char => char.charCodeAt(0));
      previewUrl = URL.createObjectURL(new Blob([bytes], { type: "application/pdf" }));
    }
    setPreviewDialog({ open: true, imageUrl: previewUrl, title, isPdf: isPdfEvidence(imageUrl, title), ownsObjectUrl: previewUrl !== imageUrl });
  };

  const closePreviewDialog = () => {
    if (previewDialog.ownsObjectUrl) URL.revokeObjectURL(previewDialog.imageUrl);
    setPreviewDialog({ open: false, imageUrl: "", title: "" });
  };

  const cancelDialogPayment = cancelPaymentDialog.payment;
  const cancelDialogPaymentUuid = cancelDialogPayment ? getPaymentUuid(cancelDialogPayment) : "";
  const isCancelPaymentReasonInvalid = cancelPaymentReasonInput.trim().length < 3;
  const resubmitDialogPayment = resubmitPaymentDialog.payment;
  const resubmitDialogPaymentUuid = resubmitDialogPayment
    ? getPaymentUuid(resubmitDialogPayment)
    : "";
  const resubmitExistingEvidenceCount = resubmitActiveEvidence.length;

  return (
    <>
      {correctionRecoveryUuid && permission.update && <Alert severity="warning" action={<Button onClick={() => setCorrectionUuid(correctionRecoveryUuid)}>ตรวจคำขอเดิม</Button>}>มีคำขอแก้ไขรับชำระที่ยังต้องตรวจผล กรุณาตรวจคำขอเดิมก่อนทำรายการใหม่</Alert>}
      {correctionUuid && <BillingPaymentCorrectionDialog key={`${correctionStorageKey}:${correctionUuid}`} uuid={correctionUuid} storageKey={correctionStorageKey} canReplace={permission.create && permission.delete && permission.update}
        onClose={() => { setCorrectionUuid(''); refreshCorrectionRecovery(); }}
        onSaved={(uuid, id) => { setCorrectionUuid(''); setCorrectionRecoveryUuid(''); setCreatedPayment({uuid,id}); setBillingDetailTab('review'); setReviewPaymentUuid(uuid); }} />}

      <NavigationBar
        title={`รายการบิลลูกค้า ${defaultCustomerName || customerInfo?.customer_name || ""}`}
        historyBack="function"
        tools={
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Chip
              label={`รอบบิล: ${activeRoundLabel}`}
              size="small"
              color="info"
              variant="outlined"
            />
            <Chip
              label={`ลูกค้า: ${defaultCustomerNo || customerInfo?.customer_code || "-"}`}
              size="small"
              color="primary"
              variant="outlined"
            />

            <TextField
              size="small"
              label="search"
              placeholder="InvoiceNo"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              sx={{ minWidth: 220 }}
            />
          </Stack>
        }
      />

      {pendingRecovery && !paymentDialog.open && <Alert severity="warning" sx={{ mt: 2 }}>
        <Stack spacing={1}>
          <Typography>มีรายการบันทึกที่ยังไม่ยืนยันผล เลขอ้างอิง {pendingRecovery.payload.client_request_id} · ยอด {formatCurrency(pendingRecovery.payload.total_paid_amount)}</Typography>
          <Button disabled={recoveryQuery.isFetching} onClick={reconcilePending}>ตรวจผลรายการเดิม</Button>
          {recoveryNotFound && <>
            <Typography>ยังไม่พบรายการที่บันทึกสำเร็จ สามารถแนบไฟล์เดิมเพื่อส่งคำขอเดิมอีกครั้ง ระบบจะตรวจป้องกันรายการซ้ำ</Typography>
            <Typography variant="caption">ไฟล์เดิม: {pendingRecovery.files.map(file => `${file.name} (${file.size} bytes)`).join(', ') || 'ไม่มีไฟล์'}</Typography>
            {!!pendingRecovery.files.length && <input type="file" multiple accept="image/*,.pdf" onChange={event => setRecoveryFiles(Array.from(event.target.files || []))} />}
            <Button disabled={!permission.create || createPaymentMutation.isLoading || recoveryFiles.length !== pendingRecovery.files.length ||
              recoveryFiles.some((file, index) => file.name !== pendingRecovery.files[index]?.name || file.size !== pendingRecovery.files[index]?.size || file.type !== pendingRecovery.files[index]?.type)}
              onClick={() => { const payload = { ...pendingRecovery.payload, files: recoveryFiles }; submission.current.restore(payload); submitPaymentPayload(payload, true); }}>ส่งรายการเดิมอีกครั้ง</Button>
          </>}
        </Stack>
      </Alert>}
      <Stack direction="row" spacing={1} sx={{ mt: 2 }} flexWrap="wrap" useFlexGap>
        <Button onClick={() => navigate(billingRelatedUrl("pdf", activeStartDate, activeEndDate, [defaultCustomerNo]))}>เตรียม PDF รอบนี้</Button>
        <Button onClick={() => navigate(billingRelatedUrl("email", activeStartDate, activeEndDate, [defaultCustomerNo]))}>จัดการอีเมลรอบนี้</Button>
        <Button disabled={!permission.read || !activeBillingRoundUuid || reportMutation.isLoading} onClick={() => reportMutation.mutate({
          customer_code: defaultCustomerNo, start_date: activeStartDate, end_date: activeEndDate,
          billing_round_uuid: activeBillingRoundUuid, status: paymentHistoryStatusFilter === "all" ? undefined : paymentHistoryStatusFilter,
        })}>Export Excel ทั้งรอบ</Button>
        <Typography variant="caption">รายงานรวม Invoice ทั้งรอบ ไม่จำกัดหน้าหรือคำค้น และใช้สถานะประวัติที่เลือก</Typography>
      </Stack>
      {invoiceError && <Alert severity="error">โหลดรายการ Invoice ไม่สำเร็จ กรุณาตรวจสิทธิ์และลองอีกครั้ง</Alert>}
      {!activeBillingRoundUuid && <Alert severity="warning" sx={{ mt: 2 }}>ไม่พบรอบบิลของลูกค้านี้ กรุณาเพิ่มรอบที่ขาดจากหน้ารายการวางบิล</Alert>}
      <Box sx={{ py: 2 }}>
        <Stack direction="row" spacing={2}>
          <Box
            sx={{
              flex: 1,
              p: 3,
              borderRadius: 3,
              bgcolor: "white",
              boxShadow: "0 1px 6px rgba(0,0,0,0.08)",
            }}
          >
            <Stack direction="row" alignItems="center" spacing={2}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 3,
                  bgcolor: "rgba(25, 118, 210, 0.08)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <ReceiptLongIcon sx={{ fontSize: 24, color: "primary.main" }} />
              </Box>
              <Box flex={1}>
                <Typography variant="body2" color="text.secondary" fontWeight={500} mb={0.5}>
                  จำนวนบิล (ชำระแล้ว/ทั้งหมด)
                </Typography>
                <Typography variant="h4" color="primary.main" fontWeight={600}>
                  {paidInvoiceCount.toLocaleString()} / {invoiceTotal.toLocaleString()}
                </Typography>
              </Box>
            </Stack>
          </Box>

          <Box
            sx={{
              flex: 1,
              p: 3,
              borderRadius: 3,
              bgcolor: "white",
              boxShadow: "0 1px 6px rgba(0,0,0,0.08)",
            }}
          >
            <Stack direction="row" alignItems="center" spacing={2}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 3,
                  bgcolor: "rgba(46, 125, 50, 0.08)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <PaidIcon sx={{ fontSize: 24, color: "success.main" }} />
              </Box>
              <Box flex={1}>
                <Typography variant="body2" color="text.secondary" fontWeight={500} mb={0.5}>
                  จำนวนเงิน (ชำระแล้ว/ทั้งหมด)
                </Typography>
                <Typography variant="h4" color="success.main" fontWeight={600}>
                  {formatCurrency(paidAmount)} / {formatCurrency(totalAmount)}
                </Typography>
              </Box>
            </Stack>
          </Box>
        </Stack>
      </Box>

      <ActionPaper
        header={{
          title: "รายการบิลและการชำระเงิน",
          caption: `${invoiceTotal.toLocaleString()} บิล · ${Number(paymentSummary?.total_payments || 0).toLocaleString()} รายการชำระเงิน`,
          flexEnd: (
            <Stack className="flex items-end flex-col" spacing={1}>
              <Box display="flex" alignItems="center" gap={1}>
                <Chip
                  label={`รอบบิล: ${activeRoundLabel}`}
                  size="small"
                  color="info"
                  variant="outlined"
                />
                <Chip
                  label={`ลูกค้า: ${defaultCustomerNo || customerInfo?.customer_code || "-"}`}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              </Box>
              {billingDetailTab === "invoices" && <Typography variant="body2">เลือกบิลด้านล่างเพื่อบันทึกหลักฐานรับชำระ</Typography>}
            </Stack>
          ),
        }}
      >
        <Box sx={{ borderBottom: "1px solid #e5e7eb", px: 2 }}>
          <Tabs
            value={billingDetailTab}
            onChange={(_, value: TBillingDetailTab) => setBillingDetailTab(value)}
            variant="scrollable"
            allowScrollButtonsMobile
          >
            <Tab value="invoices" label={`รายการบิล (${invoiceTotal.toLocaleString()})`} />
            <Tab
              value="review"
              label={`รอตรวจหลักฐาน (${Number(paymentSummary?.status_counts.submitted || 0).toLocaleString()})`}
            />
            <Tab
              value="history"
              label={`ประวัติชำระ (${Number(paymentSummary?.total_payments || 0).toLocaleString()})`}
            />
          </Tabs>
        </Box>

        {billingDetailTab === "invoices" && <Stack spacing={1} sx={{ p: 2 }}>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button variant="outlined" disabled={!permission.create || !!pendingRecovery || readSelectionMutation.isLoading || invoiceFetching || !invoiceTotal}
              onClick={selectAllMatching}>เลือกบิลที่รับชำระได้ทั้งหมดตามเงื่อนไข</Button>
            <Typography variant="caption" sx={{ alignSelf: "center" }}>checkbox หัวตารางเลือกเฉพาะหน้านี้ · เลือกทั้งเงื่อนไขจะแทนที่การเลือกเดิม</Typography>
          </Stack>
          {readSelectionMutation.isLoading && <Stack direction="row" spacing={1} alignItems="center"><CircularProgress size={18} /><Typography role="status">กำลังอ่านบิลทั้งชุดและตรวจยอดล่าสุด…</Typography></Stack>}
          {selectionNotice && <Alert severity="info" onClose={() => setSelectionNotice("")}>{selectionNotice}</Alert>}
        </Stack>}
        {createdPayment && <Alert severity="success" sx={{ m: 2 }} action={createdPayment.uuid ? <Button onClick={() => setReviewPaymentUuid(createdPayment.uuid)}>ตรวจสอบรายการนี้</Button> : undefined}>
          บันทึก {createdPayment.id} แล้ว · รอตรวจหลักฐาน ยอดหนี้ยังไม่ลดจนกว่าจะอนุมัติ
        </Alert>}
        {billingDetailTab === "invoices" && (
          <Box id="billing-company-invoice-table">
            <BasicTable
              headers={tableHeaders}
              rows={pagedData.map((row) => [
                {
                  ele: (
                    <Checkbox
                      checked={selectedInvoiceUuids.includes(row.uuid)}
                      disabled={readSelectionMutation.isLoading || !isPaymentInvoiceSelectable(row, submittedPaymentInvoiceUuidSet)}
                      onChange={() => toggleSelectOne(row)}
                      size="small"
                    />
                  ),
                  props: { align: "center" as const },
                },
                { ele: <Box>{row.invoice_number}
                  {row.document_eligible === false && <Typography fontSize={11} color="warning.main">{row.document_blocked_reason || 'รอปิดงานส่งสินค้าและหลักฐานส่ง'}</Typography>}
                  {row.delivery_completed === true && <Typography fontSize={11} color="success.main">ส่งสินค้าแล้ว · พร้อมวางบิล</Typography>}
                  {row.invoice_confirmed !== undefined && <Typography fontSize={11} color="text.secondary">{row.invoice_confirmed ? 'ยืนยัน Invoice แล้ว' : 'ยังไม่ยืนยัน Invoice สำหรับ e-Tax'}</Typography>}
                </Box>, props: { align: "left" as const } },
                {
                  ele: row.invoice_date ? formatThaiCalendarDate(row.invoice_date, "DD/MM/BBBB") : "-",
                  props: { align: "center" as const },
                },
                { ele: `${formatCurrency(row.total_amount)}`, props: { align: "right" as const } },
                { ele: `${formatCurrency(row.paid_amount)}`, props: { align: "right" as const } },
                {
                  ele: `${formatCurrency(row.outstanding_amount)}`,
                  props: {
                    align: "right" as const,
                    sx: {
                      color: row.outstanding_amount > 0 ? "error.main" : "text.primary",
                      fontWeight: 600,
                    },
                  },
                },
                {
                  ele: (
                    <Chip
                      label={
                        (row.has_pending_payment || row.pending_payment_uuid || submittedPaymentInvoiceUuidSet.has(row.uuid))
                          ? "รอตรวจโอน"
                          : getPaymentStatusLabel(row.payment_status)
                      }
                      size="small"
                      variant="outlined"
                      sx={getStatusChipSx(
                        (row.has_pending_payment || row.pending_payment_uuid || submittedPaymentInvoiceUuidSet.has(row.uuid))
                          ? "pending"
                          : getPaymentStatusTone(row.payment_status)
                      )}
                    />
                  ),
                  props: { align: "center" as const },
                },
                {
                  ele: (
                    <Stack direction="row" spacing={0.5} justifyContent="center">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => openPaymentDialog([row.uuid])}
                        disabled={!permission.create || !!pendingRecovery || invoiceFetching || !activeBillingRoundUuid ||
                          !isPaymentInvoiceSelectable(row, submittedPaymentInvoiceUuidSet)}
                        title={
                          activeBillingRoundUuid
                            ? "บันทึกการชำระเงิน"
                            : "ยังไม่พบรอบบิลที่ระบุชัดเจน"
                        }
                      >
                        <SaveOutlinedIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="secondary" title="เปิดโฟลเดอร์เอกสาร" disabled={!customerInfo?.customer_folder_url}
                        onClick={() => customerInfo?.customer_folder_url && window.open(customerInfo.customer_folder_url, "_blank", "noopener,noreferrer")}>
                        <FolderOpenOutlinedIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="info" title="จัดการอีเมล" onClick={() => navigate(billingRelatedUrl("email", activeStartDate, activeEndDate, [defaultCustomerNo]))}>
                        <EmailOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  ),
                  props: { align: "center" as const },
                },
              ])}
              isLoading={isLoading}
              limit={rowsPerPage}
            />
            <TablePagination
              component="div"
              count={invoiceTotal}
              page={tablePage}
              onPageChange={(_, newPage) => setTablePage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setTablePage(0);
              }}
              rowsPerPageOptions={[25, 50, 100]}
              labelRowsPerPage="แสดง:"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} จาก ${count}`}
            />
          </Box>
        )}

        {billingDetailTab === "review" && (
          <Box sx={{ p: 2 }}>
            {isPaymentsLoading ? (
              <Box display="flex" justifyContent="center" py={3}>
                <CircularProgress size={28} />
              </Box>
            ) : submittedPayments.length === 0 ? (
              <Alert severity="success" sx={{ "& .MuiAlert-message": { py: 0.5 } }}>
                ยังไม่มีรายการชำระเงินที่รอตรวจสอบสำหรับลูกค้าและรอบบิลนี้
              </Alert>
            ) : (
              <Stack spacing={1.25}>
                {submittedPayments.map((payment) => {
                  const paymentUuid = getPaymentUuid(payment);
                  const evidenceCount =
                    payment.evidence_files?.length || payment.evidenceFiles?.length || 0;
                  const verificationStatus = getPaymentVerificationStatus(
                    payment.verification_summary
                  );
                  return (
                    <Box
                      key={paymentUuid}
                      sx={{
                        border: "1px solid #e5e7eb",
                        borderRadius: 1.5,
                        p: 1.5,
                        bgcolor: "#fffdf5",
                      }}
                    >
                      <Stack
                        direction={{ xs: "column", md: "row" }}
                        spacing={1.5}
                        alignItems={{ xs: "stretch", md: "center" }}
                        justifyContent="space-between"
                      >
                        <Box minWidth={0}>
                          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                            <Chip
                              label="รอตรวจสอบ"
                              size="small"
                              variant="outlined"
                              sx={getStatusChipSx("warning")}
                            />
                            <Typography fontWeight={700}>{payment.payment_id}</Typography>
                            <Chip
                              label={verificationStatus.label}
                              color={verificationStatus.color}
                              size="small"
                              variant={
                                verificationStatus.color === "default" ? "outlined" : "filled"
                              }
                            />
                            <Typography fontSize={12} color="text.secondary">
                              {payment.payment_date
                                ? formatThaiCalendarDate(payment.payment_date, "DD/MM/BBBB")
                                : "-"}
                            </Typography>
                          </Stack>
                          <Typography fontSize={13} color="text.secondary" sx={{ mt: 0.5 }}>
                            {payment.invoice_count?.toLocaleString() || 0} ใบ · หลักฐาน{" "}
                            {evidenceCount.toLocaleString()} ไฟล์
                            {payment.payment_reference
                              ? ` · Ref: ${payment.payment_reference}`
                              : ""}
                          </Typography>
                        </Box>
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          justifyContent="flex-end"
                        >
                          <Typography
                            fontWeight={700}
                            color="warning.main"
                            sx={{ whiteSpace: "nowrap" }}
                          >
                            {formatCurrency(payment.total_paid_amount)}
                          </Typography>
                          <Button
                            size="small"
                            variant="contained"
                            startIcon={<ImageOutlinedIcon />}
                            onClick={() => openReviewPayment(payment)}
                          >
                            ตรวจสอบ
                          </Button>
                        </Stack>
                      </Stack>
                    </Box>
                  );
                })}
              </Stack>
            )}
          </Box>
        )}

        {billingDetailTab === "history" && (
          <Box sx={{ p: 2 }}>
            {billingPayments.length === 0 && !isPaymentsLoading && (
              <Alert severity="info" sx={{ mb: 1.5, "& .MuiAlert-message": { py: 0.5 } }}>
                ยังไม่มีรายการชำระเงินสำหรับลูกค้าและรอบบิลนี้
              </Alert>
            )}
            {Number(paymentSummary?.total_payments) > 0 && (
              <Stack spacing={1.25} sx={{ mb: 1.5 }}>
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  spacing={1}
                  alignItems={{ xs: "stretch", md: "center" }}
                  justifyContent="space-between"
                >
                  <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                    {paymentHistoryStatusOptions
                      .filter(
                        (option) =>
                          option.value === "all" ||
                          Number(paymentHistoryStatusCounts[option.value] || 0) > 0
                      )
                      .map((option) => {
                        const isActive = paymentHistoryStatusFilter === option.value;
                        const count = Number(paymentHistoryStatusCounts[option.value] || 0);
                        return (
                          <Chip
                            key={option.value}
                            label={`${option.label} ${count.toLocaleString()}`}
                            size="small"
                            color={isActive ? "primary" : "default"}
                            variant={isActive ? "filled" : "outlined"}
                            onClick={() => setPaymentHistoryStatusFilter(option.value)}
                            sx={{ fontWeight: isActive ? 700 : 500 }}
                          />
                        );
                      })}
                  </Stack>
                  <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel>สถานะประวัติ</InputLabel>
                    <Select
                      label="สถานะประวัติ"
                      value={paymentHistoryStatusFilter}
                      onChange={(event: SelectChangeEvent) =>
                        setPaymentHistoryStatusFilter(
                          event.target.value as TPaymentHistoryStatusFilter
                        )
                      }
                    >
                      {paymentHistoryStatusOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label} (
                          {Number(paymentHistoryStatusCounts[option.value] || 0).toLocaleString()})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Stack>
                <Box
                  sx={{
                    border: "1px solid #e5e7eb",
                    borderRadius: 1,
                    bgcolor: "#fafafa",
                    px: 1.5,
                    py: 1,
                  }}
                >
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={{ xs: 0.75, sm: 2 }}
                    alignItems={{ xs: "stretch", sm: "center" }}
                    justifyContent="space-between"
                  >
                    <Typography fontSize={13} color="text.secondary">
                      แสดง {filteredBillingPayments.length.toLocaleString()} รายการ
                    </Typography>
                    <Typography fontSize={13} fontWeight={700}>
                      ยอดรายการในหน้านี้ {formatCurrency(filteredBillingPaymentTotalAmount)}
                    </Typography>
                    <Typography fontSize={13} color="text.secondary">
                      Invoice {filteredBillingPaymentInvoiceCount.toLocaleString()} ใบ · หลักฐาน{" "}
                      {filteredBillingPaymentEvidenceCount.toLocaleString()} ไฟล์
                    </Typography>
                  </Stack>
                </Box>
              </Stack>
            )}
            {billingPayments.length > 0 &&
              filteredBillingPayments.length === 0 &&
              !isPaymentsLoading && (
                <Alert severity="info" sx={{ mb: 1.5, "& .MuiAlert-message": { py: 0.5 } }}>
                  ไม่มีรายการชำระเงินในสถานะที่เลือก
                </Alert>
              )}
            <BasicTable
              headers={paymentHistoryHeaders}
              isLoading={isPaymentsLoading}
              limit={filteredBillingPayments.length || 5}
              rows={filteredBillingPayments.map((payment) => {
                const paymentUuid = getPaymentUuid(payment);
                const evidenceCount =
                  payment.evidence_files?.length || payment.evidenceFiles?.length || 0;
                const verificationStatus = getPaymentVerificationStatus(
                  payment.verification_summary
                );
                const paymentAuditInfo = getBillingPaymentAuditInfo(payment);
                return [
                  {
                    ele: payment.payment_id || paymentUuid || "-",
                    props: { align: "left" as const },
                  },
                  {
                    ele: payment.payment_date
                      ? formatThaiCalendarDate(payment.payment_date, "DD/MM/BBBB")
                      : "-",
                    props: { align: "center" as const },
                  },
                  {
                    ele: formatCurrency(payment.total_paid_amount),
                    props: { align: "right" as const },
                  },
                  {
                    ele: Number(payment.invoice_count || 0).toLocaleString(),
                    props: { align: "center" as const },
                  },
                  {
                    ele: (
                      <Stack spacing={0.5} alignItems="center">
                        <Typography variant="body2">{evidenceCount.toLocaleString()}</Typography>
                        <Chip
                          label={verificationStatus.label}
                          color={verificationStatus.color}
                          size="small"
                          variant={verificationStatus.color === "default" ? "outlined" : "filled"}
                        />
                      </Stack>
                    ),
                    props: { align: "center" as const },
                  },
                  {
                    ele: (
                      <Stack spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
                        <Chip
                          label={getBillingPaymentReviewStatusLabel(payment.status)}
                          size="small"
                          variant="outlined"
                          sx={getStatusChipSx(getBillingPaymentReviewStatusTone(payment.status))}
                        />
                        {paymentAuditInfo &&
                          (paymentAuditInfo.reason ||
                            paymentAuditInfo.actor ||
                            paymentAuditInfo.occurredAt) && (
                            <Box
                              sx={{
                                width: "100%",
                                minWidth: 0,
                                maxWidth: 220,
                                bgcolor: paymentAuditInfo.bg,
                                borderRadius: 1,
                                p: 0.75,
                                textAlign: "left",
                              }}
                            >
                              {paymentAuditInfo.reason && (
                                <Typography
                                  fontSize={11}
                                  color={paymentAuditInfo.color}
                                  fontWeight={700}
                                  title={paymentAuditInfo.reason}
                                  sx={{
                                    display: "-webkit-box",
                                    WebkitBoxOrient: "vertical",
                                    WebkitLineClamp: 2,
                                    overflow: "hidden",
                                    overflowWrap: "anywhere",
                                    wordBreak: "break-word",
                                    lineHeight: 1.25,
                                  }}
                                >
                                  {paymentAuditInfo.reasonLabel}: {paymentAuditInfo.reason}
                                </Typography>
                              )}
                              {(paymentAuditInfo.actor || paymentAuditInfo.occurredAt) && (
                                <Typography
                                  fontSize={10.5}
                                  color="text.secondary"
                                  title={[
                                    paymentAuditInfo.actor
                                      ? `${paymentAuditInfo.actorLabel}: ${paymentAuditInfo.actor}`
                                      : "",
                                    paymentAuditInfo.occurredAt || "",
                                  ]
                                    .filter(Boolean)
                                    .join(" · ")}
                                  sx={{
                                    mt: paymentAuditInfo.reason ? 0.25 : 0,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {[
                                    paymentAuditInfo.actor
                                      ? `${paymentAuditInfo.actorLabel}: ${paymentAuditInfo.actor}`
                                      : "",
                                    paymentAuditInfo.occurredAt || "",
                                  ]
                                    .filter(Boolean)
                                    .join(" · ")}
                                </Typography>
                              )}
                            </Box>
                          )}
                      </Stack>
                    ),
                    props: { align: "center" as const },
                  },
                  { ele: payment.payment_reference || "-", props: { align: "left" as const } },
                  {
                    ele: (
                      <Stack direction="row" spacing={0.75} justifyContent="center" flexWrap="wrap">
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<ImageOutlinedIcon />}
                          disabled={!paymentUuid}
                          onClick={() => openReviewPayment(payment)}
                        >
                          ดูหลักฐาน
                        </Button>
                        {permission.delete && isBillingPaymentCancellable(payment.status) && (
                          <Button
                            size="small"
                            color="error"
                            variant="outlined"
                            startIcon={<CancelOutlinedIcon />}
                            disabled={!paymentUuid || deletePaymentMutation.isLoading}
                            onClick={() => openCancelPaymentDialog(payment)}
                          >
                            ยกเลิกรับชำระ
                          </Button>
                        )}
                        {permission.update && ['submitted', 'rejected'].includes(payment.status) && (
                          <Button size="small" variant="outlined" disabled={!paymentUuid || !!pendingRecovery} onClick={() => setCorrectionUuid(paymentUuid)}>แก้ไขรายการ</Button>
                        )}
                        {permission.update && permission.create && permission.delete && ['approved', 'confirmed'].includes(payment.status) && (
                          <Button size="small" color="warning" variant="outlined" disabled={!paymentUuid || !!pendingRecovery} onClick={() => setCorrectionUuid(paymentUuid)}>สร้างรายการทดแทน</Button>
                        )}
                      </Stack>
                    ),
                    props: { align: "center" as const },
                  },
                ];
              })}
            />
          </Box>
        )}
        {billingDetailTab !== "invoices" && <>
          <Stack direction="row" spacing={2} sx={{ px: 2 }} flexWrap="wrap">
            <Typography>รับชำระยืนยันแล้ว {formatCurrency(Number(paymentSummary?.approved_received_amount || 0))}</Typography>
            <Typography>รอตรวจ {formatCurrency(Number(paymentSummary?.pending_amount || 0))}</Typography>
            <Typography>ยกเลิก {formatCurrency(Number(paymentSummary?.cancelled_amount || 0))}</Typography>
          </Stack>
          <TablePagination component="div" count={paymentsResponse?.data?.pagination.total || 0} page={paymentPage}
            rowsPerPage={25} rowsPerPageOptions={[25]} onPageChange={(_, next) => setPaymentPage(next)} />
        </>}
      </ActionPaper>

      {billingDetailTab === "invoices" && selectedRows.length > 0 && <Box sx={{ position: "sticky", bottom: 0, zIndex: 5, bgcolor: "background.paper", borderTop: 1, borderColor: "divider", p: 2, boxShadow: "0 -2px 8px #00000014" }}>
        <Stack direction="row" gap={1.5} alignItems="center" flexWrap="wrap">
          <Box sx={{ flex: "1 1 240px" }}><Typography fontWeight={700}>เลือกแล้ว {selectedRows.length.toLocaleString()} ใบ · ยอดค้าง {formatCurrency(selectedTotalAmount)} บาท</Typography>
            {hiddenSelectedCount > 0 && <Typography color="warning.main" variant="body2">มี {hiddenSelectedCount} ใบที่เลือกไว้อยู่นอกคำค้นปัจจุบัน</Typography>}</Box>
          <Button disabled={readSelectionMutation.isLoading} onClick={() => setSelectionDialogOpen(true)}>ดูรายการที่เลือก</Button>
          <Button disabled={readSelectionMutation.isLoading} onClick={() => { setSelectedInvoiceUuids([]); setSelectionNotice(""); }}>ล้างการเลือกทั้งหมด</Button>
          <Button variant="contained" disabled={!permission.create || !!pendingRecovery || invoiceFetching || readSelectionMutation.isLoading || !activeBillingRoundUuid}
            onClick={() => openPaymentDialog(selectedRows.map(row => row.uuid))}>รับชำระบิลที่เลือก</Button>
        </Stack>
      </Box>}
      <Dialog open={selectionDialogOpen} onClose={() => setSelectionDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>เลือกแล้ว {selectedRows.length.toLocaleString()} ใบ · {formatCurrency(selectedTotalAmount)} บาท</DialogTitle>
        <DialogContent><BillingPaymentAllocationPreview invoices={selectedRows} amount={selectedTotalAmount}
          onRemove={uuid => setSelectedInvoiceUuids(previous => previous.filter(id => id !== uuid))} /></DialogContent>
        <DialogActions><Button onClick={() => setSelectionDialogOpen(false)}>กลับไปรายการบิล</Button></DialogActions>
      </Dialog>

      <Dialog
        open={paymentDialog.open}
        onClose={closePaymentDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { maxWidth: "calc(100vw - 48px)", overflowX: "hidden" } }}
      >
        <DialogTitle>บันทึกหลักฐานรับชำระ</DialogTitle>
        <DialogContent sx={{ overflowX: "hidden" }}>
          {submissionUncertain && <Alert severity="warning">กำลังตรวจผลการบันทึก หากการเชื่อมต่อขัดข้อง ให้กดบันทึกอีกครั้งเพื่อใช้รายการเดิม กรุณาอย่าปิดหน้านี้</Alert>}
          <Alert severity="info" sx={{ mb: 1 }}>บันทึกครั้งนี้เป็นรายการรอตรวจ ยอดหนี้จะลดหลังอนุมัติรับชำระ</Alert>
          <Box sx={{ bgcolor: "#f7faf8", p: 1.5, mb: 1, borderRadius: 1 }}><BillingPaymentSummary invoices={paymentDialogRows} amount={paymentAmountValue} /></Box>
          <fieldset disabled={submissionUncertain} style={{ border: 0, margin: 0, padding: 0 }}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            sx={{ pt: 1, minWidth: 0, maxWidth: "100%" }}
          >
            {/* ภาพหลักฐาน - Drag & Drop + File List */}
            <Box
              sx={{
                flex: "1 1 0",
                minWidth: 0,
                maxWidth: "100%",
                border: "1px solid #e5e7eb",
                borderRadius: 2,
                p: 1.5,
                overflow: "hidden",
              }}
            >
              <Typography fontWeight={600} mb={1}>
                ภาพหลักฐาน
              </Typography>

              {/* Drag & Drop Zone */}
              <Box
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                sx={{
                  border: isDragOver ? "2px dashed #1976d2" : "2px dashed #cbd5e1",
                  borderRadius: 1,
                  bgcolor: isDragOver ? "rgba(25, 118, 210, 0.04)" : "#f8fafc",
                  p: 3,
                  textAlign: "center",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  "&:hover": { borderColor: "#1976d2", bgcolor: "rgba(25, 118, 210, 0.02)" },
                }}
              >
                <CloudUploadOutlinedIcon
                  sx={{ fontSize: 40, color: isDragOver ? "primary.main" : "#94a3b8", mb: 1 }}
                />
                <Typography fontSize={14} color="text.secondary">
                  ลากไฟล์มาวางที่นี่ หรือ คลิกเพื่อเลือกไฟล์
                </Typography>
                <Typography fontSize={12} color="text.disabled">
                  รองรับไฟล์รูปภาพหรือ PDF (สามารถเลือกหลายไฟล์ได้)
                </Typography>
              </Box>
              <input
                ref={fileInputRef}
                type="file"
                hidden
                accept="image/*,.pdf"
                multiple
                onChange={handleFileInputChange}
              />

              {/* Evidence File List */}
              {evidenceFiles.length > 0 && (
                <Stack
                  spacing={1.5}
                  sx={{ mt: 2, maxHeight: 400, overflowX: "hidden", overflowY: "auto" }}
                >
                  {evidenceFiles.map((evidence, idx) => (
                    <Box
                      key={evidence.id}
                      sx={{
                        border: "1px solid #e5e7eb",
                        borderRadius: 1.5,
                        p: 1.5,
                        bgcolor: "#fafbfc",
                        minWidth: 0,
                        maxWidth: "100%",
                        overflow: "hidden",
                      }}
                    >
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={1.5}
                        alignItems={{ xs: "stretch", sm: "flex-start" }}
                        sx={{ minWidth: 0 }}
                      >
                        {/* Thumbnail */}
                        {evidence.file.type === "application/pdf" ? (
                          <Box
                            sx={{
                              width: 80,
                              height: 80,
                              borderRadius: 1,
                              border: "1px solid #e5e7eb",
                              bgcolor: "white",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                              cursor: "pointer",
                            }}
                            onClick={() => window.open(evidence.previewUrl, "_blank", "noopener")}
                          >
                            <PictureAsPdfOutlinedIcon color="error" />
                          </Box>
                        ) : (
                          <Box
                            component="img"
                            src={evidence.previewUrl}
                            alt={evidence.file.name}
                            sx={{
                              width: 80,
                              height: 80,
                              objectFit: "cover",
                              borderRadius: 1,
                              border: "1px solid #e5e7eb",
                              flexShrink: 0,
                              cursor: "pointer",
                            }}
                            onClick={() =>
                              openPreviewDialog(evidence.previewUrl, evidence.file.name)
                            }
                          />
                        )}
                        {/* Details */}
                        <Box sx={{ flex: "1 1 0", minWidth: 0, maxWidth: "100%" }}>
                          <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                            spacing={1}
                            mb={0.5}
                            sx={{ minWidth: 0 }}
                          >
                            <Typography
                              fontSize={13}
                              fontWeight={600}
                              title={evidence.file.name}
                              sx={{
                                flex: 1,
                                minWidth: 0,
                                overflow: "hidden",
                                overflowWrap: "anywhere",
                                wordBreak: "break-word",
                                display: "-webkit-box",
                                WebkitBoxOrient: "vertical",
                                WebkitLineClamp: 2,
                                lineHeight: 1.35,
                              }}
                            >
                              {idx + 1}. {evidence.file.name}
                            </Typography>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => removeEvidenceFile(evidence.id)}
                              title="ลบ"
                              sx={{ flexShrink: 0 }}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Stack>
                          <Stack direction={{ xs: "column", md: "row" }} spacing={1} sx={{ mb: 1 }}>
                            <TextField label="วันที่โอนของหลักฐานนี้" size="small" type="date" value={evidence.transferDate}
                              InputLabelProps={{ shrink: true }} error={!validPaymentDate(evidence.transferDate)}
                              onChange={event => updateEvidenceFile(evidence.id, "transferDate", event.target.value)} sx={{ flex: 1 }} />
                            <TextField label="เลขอ้างอิงของหลักฐานนี้" size="small" value={evidence.transferReference}
                              onChange={event => updateEvidenceFile(evidence.id, "transferReference", event.target.value)} sx={{ flex: 1 }} />
                          </Stack>
                          <Stack
                            direction={{ xs: "column", md: "row" }}
                            spacing={1}
                            sx={{ minWidth: 0 }}
                          >
                            <TextField
                              label="จำนวนเงิน"
                              size="small"
                              type="number"
                              value={evidence.amount}
                              onChange={(e) =>
                                updateEvidenceFile(evidence.id, "amount", e.target.value)
                              }
                              sx={{ flex: 1, minWidth: 0 }}
                              InputProps={{ sx: { fontSize: 13 } }}
                              InputLabelProps={{ sx: { fontSize: 13 } }}
                            />
                            <TextField
                              label="หมายเหตุ"
                              size="small"
                              value={evidence.note}
                              onChange={(e) =>
                                updateEvidenceFile(evidence.id, "note", e.target.value)
                              }
                              sx={{ flex: 1.5, minWidth: 0 }}
                              InputProps={{ sx: { fontSize: 13 } }}
                              InputLabelProps={{ sx: { fontSize: 13 } }}
                            />
                          </Stack>
                        </Box>
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              )}

              <Typography variant="caption" display="block" sx={{ mt: 1 }}>สูงสุด 10 ไฟล์ ไฟล์ละไม่เกิน 10 MB · แนบหลักฐานที่นับยอดการโอนแต่ละครั้งเพียงครั้งเดียว ไฟล์เดิมที่เลือกซ้ำจะไม่ถูกเพิ่ม</Typography>
              {evidenceFiles.length > 0 && evidenceValidation.errors.map(message => <Alert severity="warning" key={message} sx={{ mt: 1 }}>{message}</Alert>)}
              {evidenceFiles.length > 0 && <Typography sx={{ mt: 1 }} color={evidenceValidation.difference ? "error" : "success.main"}>ส่วนต่างจากยอดรับครั้งนี้ {formatCurrency(evidenceValidation.difference)} บาท</Typography>}
              {(evidenceValidation.multipleDates || evidenceFiles.some(file => file.transferDate !== paymentDateInput)) && <Alert severity="info" sx={{ mt: 1 }}>หลักฐานมีวันที่ต่างจากวันที่รับชำระของรายการ รายงานรับชำระใช้วันที่กลาง {paymentDateInput} หากต้องติดตามเงินเข้ารายวัน ให้แยกบันทึกแต่ละครั้งตามวันที่โอนจริง</Alert>}
              {/* Summary of evidence files */}
              {evidenceFiles.length > 0 && (
                <Box
                  sx={{
                    mt: 1.5,
                    p: 1,
                    bgcolor: "#f0f9ff",
                    borderRadius: 1,
                    border: "1px solid #bae6fd",
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography fontSize={13} color="text.secondary">
                      ไฟล์หลักฐาน: {evidenceFiles.length} รายการ
                    </Typography>
                    <Typography fontSize={13} fontWeight={600} color="primary.main">
                      รวมจากหลักฐาน: {formatCurrency(evidenceTotalAmount)}
                    </Typography>
                  </Stack>
                </Box>
              )}
              {isPaymentEvidenceRequired && evidenceFiles.length === 0 && (
                <Alert severity="warning" sx={{ mt: 1.5, "& .MuiAlert-message": { py: 0.5 } }}>
                  วิธีชำระนี้ต้องแนบหลักฐานอย่างน้อย 1 ไฟล์ก่อนบันทึก
                </Alert>
              )}
            </Box>

            {/* บันทึกข้อมูล */}
            <Box
              sx={{
                flex: "0.8 1 0",
                minWidth: 0,
                border: "1px solid #e5e7eb",
                borderRadius: 2,
                p: 1.5,
              }}
            >
              <Typography fontWeight={600} mb={1}>
                บันทึกข้อมูล
              </Typography>
              <Stack spacing={1.5}>
                {!activeBillingRoundUuid && (
                  <Alert severity="warning" sx={{ "& .MuiAlert-message": { py: 0.5 } }}>
                    ยังไม่พบรอบบิลที่ระบุชัดเจนสำหรับลูกค้ารายนี้
                    กรุณากลับไปเลือกจากรายการรอบบิลก่อนบันทึกการชำระ
                  </Alert>
                )}
                <Typography fontSize={13} color="text.secondary">
                  บิลที่เลือก: {paymentDialogRows.length.toLocaleString()} รายการ ยอดรวม:{" "}
                  {formatCurrency(paymentDialogOutstandingTotal)}
                </Typography>
                <TextField label="วันที่รับชำระของรายการ" type="date" size="small" InputLabelProps={{ shrink: true }}
                  value={paymentDateInput} onChange={event => setPaymentDateInput(event.target.value)} error={!validPaymentDate(paymentDateInput)} />
                <TextField
                  label="ยอดรับชำระครั้งนี้"
                  size="small"
                  type="number"
                  value={paymentAmountInput}
                  onChange={(event) => setPaymentAmountInput(event.target.value)}
                  error={isPaymentAmountInvalid}
                  helperText={paymentAmountHelperText}
                />
                {hasEvidenceAmounts && paymentAmountInput !== String(evidenceTotalAmount) && (
                  <Button
                    size="small"
                    variant="text"
                    onClick={() => setPaymentAmountInput(String(evidenceTotalAmount))}
                  >
                    ใช้ยอดรวมจากหลักฐาน
                  </Button>
                )}
                <FormControl size="small">
                  <InputLabel>วิธีชำระ</InputLabel>
                  <Select
                    label="วิธีชำระ"
                    value={paymentMethodInput}
                    onChange={(event: SelectChangeEvent) =>
                      setPaymentMethodInput(event.target.value as TPaymentMethod)
                    }
                  >
                    <MenuItem value="transfer">โอนเงิน</MenuItem>
                    <MenuItem value="cash">เงินสด</MenuItem>
                    <MenuItem value="cheque">เช็ค</MenuItem>
                    <MenuItem value="credit_card">บัตรเครดิต</MenuItem>
                    <MenuItem value="other">อื่นๆ</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  label="เลขอ้างอิงรวม (ถ้ามี)"
                  size="small"
                  value={paymentReferenceInput}
                  onChange={(event) => setPaymentReferenceInput(event.target.value)}
                  placeholder="เลขที่สลิป / เลขที่เช็ค"
                />
                <TextField
                  label="หมายเหตุ"
                  size="small"
                  value={paymentNotesInput}
                  onChange={(event) => setPaymentNotesInput(event.target.value)}
                  multiline
                  rows={2}
                />
              </Stack>
            </Box>
          </Stack>
          <BillingPaymentAllocationPreview invoices={paymentDialogRows} amount={paymentAmountValue} showSummary={false} />
        </fieldset>
        </DialogContent>
        <DialogActions>
          <Button disabled={submissionUncertain || createPaymentMutation.isLoading} onClick={closePaymentDialog}>ยกเลิก</Button>
          <Button
            variant="contained"
            onClick={savePayment}
            disabled={
              !permission.create || createPaymentMutation.isLoading ||
              (!submissionUncertain && (!validPaymentDate(paymentDateInput) || !activeBillingRoundUuid || isPaymentAmountInvalid || isPaymentEvidenceMissing || !allocationPreview.valid || evidenceValidation.errors.length > 0))
            }
          >
            {createPaymentMutation.isLoading ? "กำลังบันทึก..." : submissionUncertain ? "ตรวจผล / ส่งรายการเดิมอีกครั้ง" : "บันทึกหลักฐานเพื่อรอตรวจ"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!reviewPaymentUuid} onClose={closeReviewPayment} maxWidth="md" fullWidth>
        <DialogTitle>ตรวจหลักฐานการโอนเงิน</DialogTitle>
        <DialogContent>
          {isReviewPaymentLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : !reviewPayment ? (
            reviewPaymentUuid ? <Alert severity="warning">ไม่พบข้อมูลรายการชำระเงิน</Alert> : null
          ) : (
            <Stack spacing={2} sx={{ pt: 1 }}>
              <Box sx={{ border: "1px solid #e5e7eb", borderRadius: 2, p: 1.5 }}>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1.5}
                  justifyContent="space-between"
                  alignItems={{ xs: "stretch", sm: "center" }}
                >
                  <Box>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                      <Chip
                        label={getBillingPaymentReviewStatusLabel(reviewPayment.status)}
                        size="small"
                        variant="outlined"
                        sx={getStatusChipSx(
                          getBillingPaymentReviewStatusTone(reviewPayment.status)
                        )}
                      />
                      <Typography fontWeight={700}>{reviewPayment.payment_id}</Typography>
                    </Stack>
                    <Typography fontSize={13} color="text.secondary" sx={{ mt: 0.5 }}>
                      ลูกค้า {reviewPayment.customer_code}
                      {reviewPayment.customer_name ? ` · ${reviewPayment.customer_name}` : ""}
                    </Typography>
                  </Box>
                  <Box textAlign={{ xs: "left", sm: "right" }}>
                    <Typography fontSize={12} color="text.secondary">
                      ยอดโอน
                    </Typography>
                    <Typography fontWeight={800} color="warning.main" fontSize={20}>
                      {formatCurrency(reviewPayment.total_paid_amount)}
                    </Typography>
                  </Box>
                </Stack>
                <Divider sx={{ my: 1.5 }} />
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <Typography fontSize={13} color="text.secondary">
                    วันที่ชำระ:{" "}
                    {reviewPayment.payment_date
                      ? formatThaiCalendarDate(reviewPayment.payment_date, "DD/MM/BBBB")
                      : "-"}
                  </Typography>
                  <Typography fontSize={13} color="text.secondary">
                    วิธีชำระ: {reviewPayment.payment_method || "-"}
                  </Typography>
                  <Typography fontSize={13} color="text.secondary">
                    Ref: {reviewPayment.payment_reference || "-"}
                  </Typography>
                </Stack>
              </Box>

              <BillingPaymentAuditTrail payment={reviewPayment} onOpen={uuid => { closeReviewPayment(); setReviewPaymentUuid(uuid); }} />
              {permission.update && ['submitted', 'rejected'].includes(reviewPayment.status) && <Button variant="outlined" onClick={() => { const uuid = getPaymentUuid(reviewPayment); closeReviewPayment(); setCorrectionUuid(uuid); }}>แก้ไขรายการรับชำระ</Button>}
              <BillingArchivedEvidence files={reviewPayment.archived_evidence || []} onPreview={openPreviewDialog} />
              <Box sx={{ border: "1px solid #e5e7eb", borderRadius: 2, p: 1.5 }}>
                <Typography fontWeight={700} mb={1}>
                  หลักฐานการโอน ({reviewEvidenceFiles.length.toLocaleString()} ไฟล์)
                </Typography>
                {reviewEvidenceFiles.length > 0 && (
                  <Alert
                    severity={
                      reviewVerificationSummary
                        ? reviewVerificationSummary.amount_matches_payment === false
                          ? "warning"
                          : reviewVerificationSummary.amount_matches_payment === true
                            ? "success"
                            : "info"
                        : reviewEvidenceAmountState.hasEvidenceAmount
                          ? reviewEvidenceAmountState.isMismatch
                            ? "warning"
                            : "success"
                          : "info"
                    }
                    sx={{ mb: 1, "& .MuiAlert-message": { py: 0.5 } }}
                  >
                    {reviewVerificationSummary
                      ? reviewVerificationSummary.amount_matches_payment === false
                        ? `ยอดจากหลักฐานรวม ${formatCurrency(
                            Number(reviewVerificationSummary.evidence_amount_total || 0)
                          )} ไม่ตรงกับยอดชำระ ${formatCurrency(
                            reviewVerificationSummary.payment_amount
                          )} ส่วนต่าง ${formatCurrency(
                            Math.abs(Number(reviewVerificationSummary.amount_difference || 0))
                          )}`
                        : reviewVerificationSummary.amount_matches_payment === true
                          ? `ยอดจากหลักฐานรวม ${formatCurrency(
                              Number(reviewVerificationSummary.evidence_amount_total || 0)
                            )} ตรงกับยอดชำระ`
                          : "ยังไม่มียอดจาก OCR หรือ metadata ต่อไฟล์ ผู้ตรวจต้องตรวจยอดจากภาพหลักฐานด้วยตัวเอง"
                      : reviewEvidenceAmountState.hasEvidenceAmount
                        ? reviewEvidenceAmountState.isMismatch
                          ? `ยอดจากหลักฐานรวม ${formatCurrency(
                              reviewEvidenceAmountState.totalAmount
                            )} ไม่ตรงกับยอดชำระ ${formatCurrency(reviewPayment.total_paid_amount)}`
                          : `ยอดจากหลักฐานรวม ${formatCurrency(
                              reviewEvidenceAmountState.totalAmount
                            )} ตรงกับยอดชำระ`
                        : "ยังไม่มียอดจาก OCR หรือ metadata ต่อไฟล์ ผู้ตรวจต้องตรวจยอดจากภาพหลักฐานด้วยตัวเอง"}
                  </Alert>
                )}
                {reviewVerificationSummary && (
                  <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
                    <Chip
                      size="small"
                      color={reviewVerificationStatus.color}
                      variant={reviewVerificationStatus.color === "default" ? "outlined" : "filled"}
                      label={reviewVerificationStatus.label}
                    />
                    <Chip
                      size="small"
                      variant="outlined"
                      label={`ยอดในหลักฐาน ${reviewVerificationSummary.evidence_with_amount_count}/${reviewVerificationSummary.evidence_file_count} ไฟล์`}
                    />
                    {reviewVerificationSummary.missing_amount_count > 0 && (
                      <Chip
                        size="small"
                        color="warning"
                        variant="outlined"
                        label={`ยังไม่มียอด ${reviewVerificationSummary.missing_amount_count} ไฟล์`}
                      />
                    )}
                    <Chip
                      size="small"
                      variant="outlined"
                      label={formatOcrStatusCounts(reviewVerificationSummary)}
                    />
                  </Stack>
                )}
                {reviewEvidenceFiles.length === 0 ? (
                  <Alert severity="warning" sx={{ "& .MuiAlert-message": { py: 0.5 } }}>
                    ไม่มีไฟล์หลักฐานแนบมากับรายการนี้
                  </Alert>
                ) : (
                  <Stack spacing={1}>
                    {reviewEvidenceFiles.map((evidence) => {
                      const evidenceKey = getEvidenceUuid(evidence);
                      const persistedEvidenceUuid = evidence.file_uuid || evidence.uuid || "";
                      const evidenceUrl = evidence.thumbnail_url || evidence.file_url || "";
                      const isPreviewableEvidenceUrl =
                        evidenceUrl.startsWith("data:") ||
                        evidenceUrl.startsWith("blob:") ||
                        evidenceUrl.startsWith("http://") ||
                        evidenceUrl.startsWith("https://") ||
                        evidenceUrl.startsWith("/");
                      const isImageEvidence =
                        evidence.file_type?.startsWith("image/") ||
                        evidenceUrl.startsWith("data:image/") ||
                        /\.(jpe?g|png|gif|webp)$/i.test(evidence.file_name || "");
                      const canPreviewEvidence = isPreviewableEvidenceUrl && !!evidenceUrl;
                      const evidenceAmount = getEvidenceTransferAmount(evidence);
                      const ocrStatus = getEvidenceOcrStatus(evidence.ocr_status);
                      const evidenceEdit =
                        evidenceVerificationEdits[evidenceKey] ||
                        getEvidenceVerificationEdit(evidence);
                      const editedTransferAmount = Number(evidenceEdit.transfer_amount);
                      const isEditedTransferAmountInvalid =
                        evidenceEdit.transfer_amount.trim() !== "" &&
                        (!Number.isFinite(editedTransferAmount) || editedTransferAmount <= 0);
                      return (
                        <Box
                          key={evidenceKey}
                          sx={{
                            border: "1px solid #eef2f7",
                            borderRadius: 1.5,
                            p: 1,
                            bgcolor: "#fafbfc",
                          }}
                        >
                          <Stack
                            direction={{ xs: "column", md: "row" }}
                            spacing={1.5}
                            alignItems={{ xs: "stretch", md: "flex-start" }}
                          >
                            <Box
                              role={canPreviewEvidence ? "button" : undefined}
                              tabIndex={canPreviewEvidence ? 0 : undefined}
                              onClick={() =>
                                canPreviewEvidence &&
                                openPreviewDialog(evidenceUrl, evidence.file_name)
                              }
                              onKeyDown={(event) => {
                                if (
                                  canPreviewEvidence &&
                                  (event.key === "Enter" || event.key === " ")
                                ) {
                                  event.preventDefault();
                                  openPreviewDialog(evidenceUrl, evidence.file_name);
                                }
                              }}
                              sx={{
                                width: { xs: "100%", md: 150 },
                                height: { xs: 150, md: 116 },
                                borderRadius: 1.5,
                                border: "1px solid #e5e7eb",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                bgcolor: "white",
                                flexShrink: 0,
                                overflow: "hidden",
                                position: "relative",
                                cursor: canPreviewEvidence ? "pointer" : "default",
                                "&:hover .evidence-preview-overlay": {
                                  opacity: canPreviewEvidence ? 1 : 0,
                                },
                              }}
                            >
                              {canPreviewEvidence && isImageEvidence ? (
                                <Box
                                  component="img"
                                  src={evidenceUrl}
                                  alt={evidence.file_name}
                                  sx={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "cover",
                                  }}
                                />
                              ) : evidence.file_type === "application/pdf" ? (
                                <PictureAsPdfOutlinedIcon color="error" sx={{ fontSize: 34 }} />
                              ) : (
                                <ImageOutlinedIcon color="action" sx={{ fontSize: 34 }} />
                              )}
                              {canPreviewEvidence && (
                                <Box
                                  className="evidence-preview-overlay"
                                  sx={{
                                    position: "absolute",
                                    inset: 0,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    bgcolor: "rgba(15, 23, 42, 0.38)",
                                    color: "white",
                                    opacity: 0,
                                    transition: "opacity 0.15s ease",
                                  }}
                                >
                                  <ZoomInOutlinedIcon />
                                </Box>
                              )}
                            </Box>
                            <Box flex={1} minWidth={0}>
                              <Typography fontSize={13} fontWeight={700} noWrap>
                                {evidence.file_name}
                              </Typography>
                              <Typography fontSize={12} color="text.secondary">
                                {getEvidenceUploadedAt(evidence)
                                  ? `อัปโหลด ${formatBangkokDateTime(getEvidenceUploadedAt(evidence), "DD/MM/BBBB HH:mm")}`
                                  : "ยังไม่มีเวลาอัปโหลด"}
                              </Typography>
                              <Stack
                                direction="row"
                                spacing={0.75}
                                flexWrap="wrap"
                                useFlexGap
                                sx={{ mt: 0.75 }}
                              >
                                <Chip
                                  size="small"
                                  variant="outlined"
                                  label={`แหล่งที่มา: ${formatEvidenceSource(evidence.source)}`}
                                />
                                <Chip
                                  size="small"
                                  color={ocrStatus.color}
                                  variant={ocrStatus.color === "default" ? "outlined" : "filled"}
                                  label={`OCR: ${ocrStatus.label}`}
                                />
                                {evidence.ocr_confidence !== undefined &&
                                  evidence.ocr_confidence !== null && (
                                    <Chip
                                      size="small"
                                      variant="outlined"
                                      label={`ความมั่นใจ ${Math.round(
                                        Number(evidence.ocr_confidence) * 100
                                      )}%`}
                                    />
                                  )}
                              </Stack>
                              <Stack
                                direction={{ xs: "column", sm: "row" }}
                                spacing={1.5}
                                sx={{ mt: 0.75 }}
                              >
                                <Typography fontSize={12} color="text.secondary">
                                  ยอดจากหลักฐาน:{" "}
                                  {evidenceAmount !== null ? formatCurrency(evidenceAmount) : "-"}
                                </Typography>
                                <Typography fontSize={12} color="text.secondary">
                                  วันที่โอน:{" "}
                                  {evidence.transfer_date
                                    ? formatThaiCalendarDate(evidence.transfer_date, "DD/MM/BBBB")
                                    : "-"}
                                </Typography>
                                <Typography fontSize={12} color="text.secondary">
                                  Ref: {evidence.transfer_reference || "-"}
                                </Typography>
                              </Stack>
                              {(evidence.verified_by || evidence.verified_at) && (
                                <Stack
                                  direction={{ xs: "column", sm: "row" }}
                                  spacing={1.5}
                                  sx={{ mt: 0.5 }}
                                >
                                  <Typography fontSize={12} color="text.secondary">
                                    ตรวจโดย: {evidence.verified_by || "-"}
                                  </Typography>
                                  <Typography fontSize={12} color="text.secondary">
                                    ตรวจเมื่อ:{" "}
                                    {evidence.verified_at
                                      ? formatBangkokDateTime(evidence.verified_at, "DD/MM/BBBB HH:mm")
                                      : "-"}
                                  </Typography>
                                </Stack>
                              )}
                              {evidence.notes && (
                                <Typography fontSize={12} color="text.secondary" sx={{ mt: 0.25 }}>
                                  หมายเหตุ: {evidence.notes}
                                </Typography>
                              )}
                              <Stack
                                direction={{ xs: "column", md: "row" }}
                                spacing={1}
                                alignItems={{ xs: "stretch", md: "flex-start" }}
                                sx={{ mt: 1 }}
                              >
                                <TextField
                                  label="ยอดตรวจ"
                                  size="small"
                                  type="number"
                                  value={evidenceEdit.transfer_amount}
                                  error={isEditedTransferAmountInvalid}
                                  helperText={
                                    isEditedTransferAmountInvalid ? "ยอดไม่ถูกต้อง" : undefined
                                  }
                                  onChange={(event) =>
                                    updateEvidenceVerificationField(
                                      evidenceKey,
                                      "transfer_amount",
                                      event.target.value
                                    )
                                  }
                                  sx={{ minWidth: 130 }}
                                />
                                <TextField
                                  label="วันที่โอน"
                                  size="small"
                                  type="date"
                                  value={evidenceEdit.transfer_date}
                                  InputLabelProps={{ shrink: true }}
                                  onChange={(event) =>
                                    updateEvidenceVerificationField(
                                      evidenceKey,
                                      "transfer_date",
                                      event.target.value
                                    )
                                  }
                                  sx={{ minWidth: 150 }}
                                />
                                <TextField
                                  label="Ref"
                                  size="small"
                                  value={evidenceEdit.transfer_reference}
                                  onChange={(event) =>
                                    updateEvidenceVerificationField(
                                      evidenceKey,
                                      "transfer_reference",
                                      event.target.value
                                    )
                                  }
                                  sx={{ minWidth: 150 }}
                                />
                              </Stack>
                              <Stack
                                direction={{ xs: "column", md: "row" }}
                                spacing={1}
                                alignItems={{ xs: "stretch", md: "center" }}
                                sx={{ mt: 1 }}
                              >
                                <TextField
                                  label="หมายเหตุผลตรวจ"
                                  size="small"
                                  value={evidenceEdit.notes}
                                  onChange={(event) =>
                                    updateEvidenceVerificationField(
                                      evidenceKey,
                                      "notes",
                                      event.target.value
                                    )
                                  }
                                  sx={{ flex: 1 }}
                                />
                                <FormControlLabel label="ตรวจหลักฐานนี้แล้ว" control={<Checkbox checked={!!evidenceChecked[evidenceKey]}
                                  disabled={!permission.approve || reviewPayment.status !== "submitted"}
                                  onChange={event => setEvidenceChecked(prev => ({ ...prev, [evidenceKey]: event.target.checked }))} />} />
                                <Button
                                  size="small"
                                  variant="contained"
                                  disabled={
                                    !permission.approve || reviewPayment.status !== "submitted" || !evidenceChecked[evidenceKey] || !evidenceEdit.transfer_amount.trim() || !validPaymentDate(evidenceEdit.transfer_date) ||
                                    !persistedEvidenceUuid ||
                                    isEditedTransferAmountInvalid ||
                                    updateEvidenceVerificationMutation.isLoading
                                  }
                                  onClick={() => saveEvidenceVerification(evidence)}
                                >
                                  {updateEvidenceVerificationMutation.isLoading
                                    ? "กำลังบันทึก..."
                                    : "บันทึกผลตรวจ"}
                                </Button>
                              </Stack>
                            </Box>
                          </Stack>
                        </Box>
                      );
                    })}
                  </Stack>
                )}
              </Box>

              <Box sx={{ border: "1px solid #e5e7eb", borderRadius: 2, p: 1.5 }}>
                <Typography fontWeight={700} mb={1}>
                  รายการ Invoice ที่จะรับชำระ
                </Typography>
                <Stack spacing={0.75}>
                  {reviewInvoicePayments.map((allocation) => (
                    <Stack
                      key={allocation.invoice_uuid}
                      direction="row"
                      spacing={1}
                      justifyContent="space-between"
                      sx={{ borderBottom: "1px solid #eef2f7", pb: 0.75 }}
                    >
                      <Box minWidth={0}>
                        <Typography fontSize={13} fontWeight={700} noWrap>
                          {allocation.invoice_number}
                        </Typography>
                        <Typography fontSize={12} color="text.secondary">
                          สถานะปัจจุบัน: {allocation.payment_status || "-"}
                        </Typography>
                      </Box>
                      <Typography fontSize={13} fontWeight={700} color="primary.main">
                        {formatCurrency(allocation.this_payment ?? allocation.allocated_amount)}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </Box>

              <TextField
                label="เหตุผลกรณีไม่อนุมัติ"
                size="small"
                value={rejectReasonInput}
                onChange={(event) => setRejectReasonInput(event.target.value)}
                multiline
                rows={2}
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeReviewPayment}>ปิด</Button>
          <Button
            color="error"
            variant="outlined"
            startIcon={<CancelOutlinedIcon />}
            onClick={rejectReviewPayment}
            disabled={
              !reviewPayment ||
              reviewPayment.status !== "submitted" ||
              !permission.approve || rejectReasonInput.trim().length < 3 || approvePaymentMutation.isLoading ||
              rejectPaymentMutation.isLoading
            }
          >
            {rejectPaymentMutation.isLoading ? "กำลังบันทึก..." : "ไม่อนุมัติ"}
          </Button>
          <Button
            color="success"
            variant="contained"
            startIcon={<CheckCircleOutlineIcon />}
            onClick={approveReviewPayment}
            disabled={
              !reviewPayment ||
              reviewPayment.status !== "submitted" ||
              shouldBlockReviewApproval ||
              !permission.approve || rejectPaymentMutation.isLoading ||
              approvePaymentMutation.isLoading
            }
          >
            {approvePaymentMutation.isLoading ? "กำลังอนุมัติ..." : "อนุมัติรับชำระ"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={resubmitPaymentDialog.open}
        onClose={updatePaymentMutation.isLoading ? undefined : closeResubmitPaymentDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>ส่งหลักฐานตรวจใหม่</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            <Alert severity="info">
              รายการนี้ถูกไม่อนุมัติไว้ก่อนหน้า การส่งตรวจใหม่จะเปลี่ยนสถานะกลับเป็นรอตรวจสอบ
              โดยคงยอดรับชำระและรายการ Invoice เดิมไว้ หลักฐานเดิมจะเก็บในประวัติ
              หากยอดเดิมผิด ให้สร้างรายการใหม่โดยเก็บรายการที่ไม่อนุมัติไว้ตรวจย้อนหลัง
            </Alert>
            {resubmitDetailLoading && <CircularProgress size={24} />}
            <FormControlLabel label="แทนที่หลักฐานเดิมด้วยไฟล์ใหม่ และเก็บหลักฐานเดิมในประวัติ"
              control={<Checkbox checked={replaceEvidenceConfirmed} onChange={event => setReplaceEvidenceConfirmed(event.target.checked)} />} />

            <Box sx={{ border: "1px solid #e5e7eb", borderRadius: 1.5, p: 1.5 }}>
              <Stack spacing={0.75}>
                <Stack direction="row" justifyContent="space-between" spacing={2}>
                  <Typography fontSize={13} color="text.secondary">
                    เลขที่รับชำระ
                  </Typography>
                  <Typography fontSize={13} fontWeight={700}>
                    {resubmitDialogPayment?.payment_id || resubmitDialogPaymentUuid || "-"}
                  </Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between" spacing={2}>
                  <Typography fontSize={13} color="text.secondary">
                    ยอดรับชำระ
                  </Typography>
                  <Typography fontSize={13} fontWeight={700}>
                    {formatCurrency(Number(resubmitDialogPayment?.total_paid_amount || 0))}
                  </Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between" spacing={2}>
                  <Typography fontSize={13} color="text.secondary">
                    หลักฐานเดิม
                  </Typography>
                  <Typography fontSize={13} fontWeight={700}>
                    {resubmitExistingEvidenceCount.toLocaleString()} ไฟล์
                  </Typography>
                </Stack>
                {resubmitDialogPayment?.rejection_reason && (
                  <Alert severity="error" sx={{ mt: 1 }}>
                    เหตุผลที่ไม่อนุมัติ: {resubmitDialogPayment.rejection_reason}
                  </Alert>
                )}
              </Stack>
            </Box>

            <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
              <TextField
                label="เลขอ้างอิงการชำระ"
                size="small"
                value={resubmitPaymentReferenceInput}
                onChange={(event) => setResubmitPaymentReferenceInput(event.target.value)}
                sx={{ flex: 1 }}
              />
              <TextField
                label="หมายเหตุ"
                size="small"
                value={resubmitPaymentNotesInput}
                onChange={(event) => setResubmitPaymentNotesInput(event.target.value)}
                sx={{ flex: 1.5 }}
              />
            </Stack>

            <Box sx={{ border: "1px solid #e5e7eb", borderRadius: 1.5, p: 1.5 }}>
              <Stack spacing={1.5}>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1}
                  alignItems={{ xs: "stretch", sm: "center" }}
                  justifyContent="space-between"
                >
                  <Box>
                    <Typography fontWeight={700}>หลักฐานใหม่</Typography>
                    <Typography fontSize={12} color="text.secondary">
                      ต้องแนบอย่างน้อย 1 ไฟล์ เพื่อส่งกลับไปรอตรวจสอบ
                    </Typography>
                  </Box>
                  <Button
                    variant="outlined"
                    startIcon={<CloudUploadOutlinedIcon />}
                    onClick={() => resubmitFileInputRef.current?.click()}
                  >
                    เลือกไฟล์
                  </Button>
                </Stack>
                <input
                  ref={resubmitFileInputRef}
                  type="file"
                  hidden
                  accept="image/*,.pdf"
                  multiple
                  onChange={handleResubmitFileInputChange}
                />

                {resubmitEvidenceFiles.length === 0 ? (
                  <Alert severity="warning" sx={{ "& .MuiAlert-message": { py: 0.5 } }}>
                    ยังไม่ได้แนบหลักฐานใหม่
                  </Alert>
                ) : (
                  <Stack spacing={1}>
                    {resubmitEvidenceFiles.map((evidence, index) => (
                      <Box
                        key={evidence.id}
                        sx={{
                          border: "1px solid #e5e7eb",
                          borderRadius: 1,
                          p: 1,
                          bgcolor: "#fafbfc",
                          minWidth: 0,
                        }}
                      >
                        <Stack
                          direction="row"
                          spacing={1.25}
                          alignItems="center"
                          sx={{ minWidth: 0 }}
                        >
                          {evidence.file.type === "application/pdf" ? (
                            <Box
                              sx={{
                                width: 48,
                                height: 48,
                                borderRadius: 1,
                                border: "1px solid #e5e7eb",
                                bgcolor: "white",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0,
                              }}
                            >
                              <PictureAsPdfOutlinedIcon color="error" />
                            </Box>
                          ) : (
                            <Box
                              component="img"
                              src={evidence.previewUrl}
                              alt={evidence.file.name}
                              sx={{
                                width: 48,
                                height: 48,
                                objectFit: "cover",
                                borderRadius: 1,
                                border: "1px solid #e5e7eb",
                                flexShrink: 0,
                              }}
                            />
                          )}
                          <Typography
                            fontSize={13}
                            fontWeight={600}
                            title={evidence.file.name}
                            sx={{
                              flex: 1,
                              minWidth: 0,
                              overflow: "hidden",
                              overflowWrap: "anywhere",
                              wordBreak: "break-word",
                              display: "-webkit-box",
                              WebkitBoxOrient: "vertical",
                              WebkitLineClamp: 2,
                              lineHeight: 1.35,
                            }}
                          >
                            {index + 1}. {evidence.file.name}
                          </Typography>
                          <IconButton
                            size="small"
                            color="error"
                            title="ลบไฟล์"
                            onClick={() => removeResubmitEvidenceFile(evidence.id)}
                            sx={{ flexShrink: 0 }}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      </Box>
                    ))}
                  </Stack>
                )}
              </Stack>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeResubmitPaymentDialog} disabled={updatePaymentMutation.isLoading}>
            ปิด
          </Button>
          <Button
            color="warning"
            variant="contained"
            startIcon={<CloudUploadOutlinedIcon />}
            onClick={confirmResubmitPayment}
            disabled={
              !permission.update || !replaceEvidenceConfirmed || resubmitDetailLoading || !resubmitDetailResponse?.data ||
              !resubmitDialogPaymentUuid ||
              resubmitEvidenceFiles.length === 0 ||
              updatePaymentMutation.isLoading
            }
          >
            {updatePaymentMutation.isLoading ? "กำลังส่งตรวจ..." : "ส่งตรวจใหม่"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={cancelPaymentDialog.open}
        onClose={deletePaymentMutation.isLoading ? undefined : closeCancelPaymentDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>ยกเลิกรับชำระ</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            <Alert severity="warning">
              การยกเลิกรายการรับชำระจะ revert ยอดชำระที่เคย post เข้า Invoice แล้ว
              และรายการนี้จะถูกเก็บเป็นสถานะยกเลิกสำหรับตรวจสอบย้อนหลัง
            </Alert>

            <Box sx={{ border: "1px solid #e5e7eb", borderRadius: 1.5, p: 1.5 }}>
              <Stack spacing={0.75}>
                <Stack direction="row" justifyContent="space-between" spacing={2}>
                  <Typography fontSize={13} color="text.secondary">
                    เลขที่รับชำระ
                  </Typography>
                  <Typography fontSize={13} fontWeight={700}>
                    {cancelDialogPayment?.payment_id || cancelDialogPaymentUuid || "-"}
                  </Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between" spacing={2}>
                  <Typography fontSize={13} color="text.secondary">
                    ยอดรับชำระ
                  </Typography>
                  <Typography fontSize={13} fontWeight={700} color="error.main">
                    {formatCurrency(Number(cancelDialogPayment?.total_paid_amount || 0))}
                  </Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between" spacing={2}>
                  <Typography fontSize={13} color="text.secondary">
                    Invoice ที่เกี่ยวข้อง
                  </Typography>
                  <Typography fontSize={13} fontWeight={700}>
                    {Number(cancelDialogPayment?.invoice_count || 0).toLocaleString()} ใบ
                  </Typography>
                </Stack>
              </Stack>
            </Box>

            <TextField
              label="เหตุผลการยกเลิก"
              placeholder="เช่น เลือกหลักฐานผิด / ยอดโอนผิด / ต้องบันทึกใหม่"
              value={cancelPaymentReasonInput}
              onChange={(event) => setCancelPaymentReasonInput(event.target.value)}
              multiline
              rows={3}
              required
              error={cancelPaymentReasonInput.length > 0 && isCancelPaymentReasonInvalid}
              helperText={
                cancelPaymentReasonInput.length > 0 && isCancelPaymentReasonInvalid
                  ? "กรุณาระบุเหตุผลอย่างน้อย 3 ตัวอักษร"
                  : "เหตุผลนี้จะถูกบันทึกไว้ในประวัติการยกเลิกรับชำระ"
              }
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCancelPaymentDialog} disabled={deletePaymentMutation.isLoading}>
            ปิด
          </Button>
          <Button
            color="error"
            variant="contained"
            startIcon={<CancelOutlinedIcon />}
            onClick={confirmCancelPayment}
            disabled={
              !cancelDialogPaymentUuid ||
              isCancelPaymentReasonInvalid ||
              deletePaymentMutation.isLoading
            }
          >
            {deletePaymentMutation.isLoading ? "กำลังยกเลิก..." : "ยืนยันยกเลิกรับชำระ"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={previewDialog.open}
        onClose={closePreviewDialog}
        maxWidth="xl"
        fullWidth
        PaperProps={{ sx: { overflow: "hidden" } }}
      >
        <DialogTitle sx={{ pb: 1.5 }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            alignItems={{ xs: "stretch", sm: "center" }}
            justifyContent="space-between"
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography fontSize={20} fontWeight={700}>
                ดูหลักฐานการโอน
              </Typography>
              <Typography
                fontSize={13}
                color="text.secondary"
                title={previewDialog.title}
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  maxWidth: { xs: "100%", md: 760 },
                }}
              >
                {previewDialog.title}
              </Typography>
            </Box>
            <Chip
              size="small"
              icon={<ImageOutlinedIcon />}
              label={`ขนาดภาพ ${Math.round(zoomLevel * 100)}%`}
              sx={{ alignSelf: { xs: "flex-start", sm: "center" } }}
            />
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ pt: 0, overflow: "hidden" }}>
          <Stack spacing={1.5}>
            <Box
              sx={{
                border: "1px solid #e5e7eb",
                borderRadius: 1.5,
                bgcolor: "#f8fafc",
                px: 1.5,
                py: 1,
              }}
            >
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1.5}
                alignItems={{ xs: "stretch", sm: "center" }}
              >
                <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1 }}>
                  <ZoomInOutlinedIcon fontSize="small" color="action" />
                  <Typography fontSize={13} color="text.secondary" sx={{ minWidth: 56 }}>
                    ซูมภาพ
                  </Typography>
                  <Slider
                    min={0.5}
                    max={3}
                    step={0.1}
                    value={zoomLevel}
                    onChange={(_, value) => setZoomLevel(value as number)}
                    sx={{ flex: 1 }}
                  />
                  <Typography
                    fontSize={13}
                    color="text.secondary"
                    sx={{ minWidth: 44, textAlign: "right" }}
                  >
                    {Math.round(zoomLevel * 100)}%
                  </Typography>
                </Stack>
                <Button size="small" variant="outlined" onClick={() => setZoomLevel(1)}>
                  รีเซ็ต
                </Button>
              </Stack>
            </Box>

            <Box
              sx={{
                border: "1px solid #e5e7eb",
                borderRadius: 1.5,
                bgcolor: "#f8fafc",
                height: { xs: "58vh", md: "68vh" },
                overflow: "auto",
              }}
            >
              <Box
                sx={{
                  minWidth: "100%",
                  minHeight: "100%",
                  p: 1,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "flex-start",
                }}
              >
                {previewDialog.isPdf ?
                  <object data={previewDialog.imageUrl} type="application/pdf" width="100%" height="600"><a href={previewDialog.imageUrl} target="_blank" rel="noreferrer">เปิด PDF</a></object> : <Box
                  component="img"
                  src={previewDialog.imageUrl}
                  alt="preview"
                  sx={{
                    width: `${zoomLevel * 100}%`,
                    maxWidth: zoomLevel <= 1 ? "100%" : "none",
                    height: "auto",
                    display: "block",
                    borderRadius: 1,
                    objectFit: "contain",
                  }}
                />}
              </Box>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button component="a" href={previewDialog.imageUrl} target="_blank" rel="noreferrer">เปิดไฟล์</Button>
          <Button onClick={closePreviewDialog}>ปิด</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default BillingCompanyInvoiceListPage;
